export interface ServiceData {
  id: string;
  slug: string;
  name: string;
  category: string;
  categoryId: string;
  icon: string;
  outcomeHeadline: string;
  subhead: string;
  typicalHours: string;
  bestFor: string[];
  notIdealFor: string[];
  prerequisites: string[];
  whatYouGet: string[];
  deliverables: string[];
  modules: {
    name: string;
    description: string;
    typicalHours: string;
  }[];
  exampleSprint: {
    week: number;
    title: string;
    description: string;
  }[];
  testimonial?: {
    quote: string;
    name: string;
    title: string;
    company: string;
  };
  faq: {
    id: string;
    question: string;
    answer: string;
  }[];
  capacityFit: 'light' | 'standard' | 'heavy';
  relatedServices: string[];
}

export const servicesData: ServiceData[] = [
  {
    id: 'paid-search',
    slug: 'paid-search',
    name: 'Paid Search',
    category: 'Digital Marketing',
    categoryId: 'digital-marketing',
    icon: 'MagnifyingGlassIcon',
    outcomeHeadline: 'Capture high-intent buyers actively searching for your solution',
    subhead: 'Strategic Google Ads and Bing campaigns that turn search traffic into qualified leads and revenue.',
    typicalHours: '10-16h',
    bestFor: [
      'Businesses with proven product-market fit',
      'High-intent search volume in your category',
      'Budget of $5k+/month for ad spend'
    ],
    notIdealFor: [
      'Brand-new products with no search demand',
      'Extremely niche markets with low volume'
    ],
    prerequisites: [
      'Google Ads account access',
      'Conversion tracking setup or willingness to implement',
      'Landing pages or website ready'
    ],
    whatYouGet: [
      'Qualified leads from high-intent search traffic',
      'Predictable cost-per-acquisition data',
      'Full visibility into keyword performance'
    ],
    deliverables: [
      'Keyword research and competitive analysis',
      'Campaign structure and ad group setup',
      'Ad copywriting (5-10 variations per group)',
      'Bid management and budget optimization',
      'Landing page conversion tracking setup',
      'Negative keyword management',
      'Monthly performance reporting with insights',
      'Ongoing optimization and A/B testing'
    ],
    modules: [
      { name: 'Landing Page Build', description: 'Custom landing page optimized for search traffic', typicalHours: '20-30h' },
      { name: 'Analytics Setup', description: 'GA4, GTM, and conversion tracking implementation', typicalHours: '8-12h' },
      { name: 'Creative Batch', description: 'Display ad creative for GDN expansion', typicalHours: '10-15h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Setup', description: 'Keyword research, competitor analysis, account audit, and campaign structure planning' },
      { week: 2, title: 'Campaign Build', description: 'Ad copywriting, campaign setup, tracking implementation, and initial launch' },
      { week: 3, title: 'Optimization', description: 'Bid adjustments, negative keyword additions, ad testing, and performance monitoring' },
      { week: 4, title: 'Reporting & Scale', description: 'Performance analysis, budget reallocation, and recommendations for next sprint' }
    ],
    testimonial: {
      quote: 'Daniel is one of the most savvy and creative SEO and online marketers I have come across. He has a firm grasp of SEO and all online marketing activities designed to increase website traffic.',
      name: 'Mark Subel',
      title: 'Digital Marketing Consultant',
      company: 'Focal Click'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. We work in sprints with capacity buckets. Paid Search typically fits within Execution Coverage (40h/mo) or Growth Partner for heavier campaigns. You pay for the bucket, not the hour.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Strategy, campaign setup, copywriting, optimization, reporting, and client communication. Ad spend is separate and paid directly to Google/Bing.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present campaign strategy and ad copy for approval before launch. Ongoing optimizations happen within the sprint scope with weekly updates.' },
      { id: 'faq-4', question: 'What if I need a heavy project month?', answer: 'Photoshoots, landing page builds, or major campaign launches are scoped separately. We plan these in advance and allocate capacity accordingly.' },
      { id: 'faq-5', question: 'Do you guarantee results?', answer: 'We guarantee execution quality and best practices, not specific ROAS. Performance depends on offer, market, and budget. We optimize for your goals.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Google Ads spend (your budget), landing page hosting if needed, and any premium tools (e.g., SEMrush) if not already in your stack.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We integrate with your internal team or other agencies. Clear communication and shared goals make it work.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['paid-social', 'seo', 'landing-page-build']
  },
  {
    id: 'paid-social',
    slug: 'paid-social',
    name: 'Paid Social',
    category: 'Digital Marketing',
    categoryId: 'digital-marketing',
    icon: 'MegaphoneIcon',
    outcomeHeadline: 'Reach your ideal customers where they spend their time',
    subhead: 'Targeted social advertising across Meta, LinkedIn, and TikTok that drives awareness, engagement, and conversions.',
    typicalHours: '12-20h',
    bestFor: [
      'Visual brands with strong creative assets',
      'B2C and B2B companies with defined audiences',
      'Businesses ready to test and iterate'
    ],
    notIdealFor: [
      'Brands without visual content or creative',
      'Extremely limited budgets (<$3k/month)'
    ],
    prerequisites: [
      'Meta Business Manager or LinkedIn Campaign Manager access',
      'Brand assets (logos, product images, or willingness to create)',
      'Landing pages or website ready'
    ],
    whatYouGet: [
      'Targeted reach to your ideal customer profile',
      'Creative testing data to inform future campaigns',
      'Multi-platform presence and brand awareness'
    ],
    deliverables: [
      'Audience research and targeting strategy',
      'Campaign setup across platforms (Meta, LinkedIn, TikTok)',
      'Ad creative development (static and video)',
      'A/B testing framework and execution',
      'Budget optimization and bid management',
      'Pixel/conversion tracking setup',
      'Monthly performance reporting',
      'Creative refresh and ongoing optimization'
    ],
    modules: [
      { name: 'Photoshoot Module', description: 'Professional product or lifestyle shoot for ad creative', typicalHours: '25-40h' },
      { name: 'Video Production', description: 'Short-form video ads (15-30s) for social platforms', typicalHours: '20-35h' },
      { name: 'Landing Page Build', description: 'Custom landing page for social traffic', typicalHours: '20-30h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Creative', description: 'Audience research, creative brief, asset collection, and campaign planning' },
      { week: 2, title: 'Campaign Launch', description: 'Ad creative production, campaign setup, tracking implementation, and launch' },
      { week: 3, title: 'Test & Optimize', description: 'A/B testing, audience refinement, creative iteration, and performance monitoring' },
      { week: 4, title: 'Scale & Report', description: 'Budget reallocation to winners, performance analysis, and next sprint planning' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. We work in sprints with capacity buckets. Paid Social typically fits within Execution Coverage (40h/mo) or Growth Partner for multi-platform campaigns.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Strategy, creative production, campaign setup, optimization, reporting, and communication. Ad spend is separate.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present creative and targeting strategy for approval before launch. Ongoing optimizations happen within sprint scope with weekly check-ins.' },
      { id: 'faq-4', question: 'What if I need a photoshoot or heavy project month?', answer: 'Photoshoots and video production are scoped as separate modules. We plan these in advance and allocate capacity accordingly.' },
      { id: 'faq-5', question: 'Do you guarantee results?', answer: 'We guarantee execution quality and platform best practices, not specific ROAS. Performance depends on creative, offer, and market fit.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Ad spend (your budget), stock footage or music licenses if needed, and platform fees (e.g., Meta Business Suite).' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with internal teams, creative agencies, and other partners. Clear workflows make it seamless.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['photo-video', 'content', 'paid-search']
  },
  {
    id: 'seo',
    slug: 'seo',
    name: 'SEO',
    category: 'Digital Marketing',
    categoryId: 'digital-marketing',
    icon: 'ChartBarIcon',
    outcomeHeadline: 'Build sustainable organic traffic that compounds over time',
    subhead: 'Technical SEO, content strategy, and link building that improves rankings and drives qualified traffic.',
    typicalHours: '15-25h',
    bestFor: [
      'Businesses with long sales cycles',
      'Content-heavy sites (blogs, resources, tools)',
      'Companies committed to 6+ month timelines'
    ],
    notIdealFor: [
      'Businesses needing immediate traffic',
      'Sites with fundamental technical issues (requires dev work first)'
    ],
    prerequisites: [
      'Website with CMS access',
      'Google Search Console and Analytics access',
      'Willingness to publish content regularly'
    ],
    whatYouGet: [
      'Sustainable organic traffic growth',
      'Improved search visibility and rankings',
      'Long-term ROI that compounds'
    ],
    deliverables: [
      'Technical SEO audit and fixes',
      'Keyword research and content strategy',
      'On-page optimization (meta tags, headers, internal linking)',
      'Content briefs and optimization',
      'Link building strategy and outreach',
      'Local SEO setup (if applicable)',
      'Monthly ranking and traffic reports',
      '90-day implementation roadmap'
    ],
    modules: [
      { name: 'Content Production', description: 'SEO-optimized blog posts and landing pages', typicalHours: '8-15h per piece' },
      { name: 'Technical SEO Deep Dive', description: 'Advanced technical fixes (schema, speed, crawlability)', typicalHours: '15-25h' },
      { name: 'Link Building Campaign', description: 'Outreach and link acquisition for authority building', typicalHours: '20-30h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Audit & Strategy', description: 'Technical audit, keyword research, competitor analysis, and roadmap creation' },
      { week: 2, title: 'Technical Fixes', description: 'On-page optimization, meta tag updates, schema implementation, and internal linking' },
      { week: 3, title: 'Content & Links', description: 'Content brief creation, optimization of existing pages, and link building outreach' },
      { week: 4, title: 'Reporting & Planning', description: 'Ranking tracking, traffic analysis, and next sprint prioritization' }
    ],
    testimonial: {
      quote: 'Thanks to their extensive knowledge of SEO, exquisite lateral think and innovating promotional ideas, the professional and friendly team of Best Odds Corp is a great partner to collaborate with.',
      name: 'Switch Affiliate Team',
      title: 'Partner Team',
      company: 'Switch Partner Team'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. SEO work is sprint-based. Typical engagement is Execution Coverage (40h/mo) or Growth Partner for aggressive campaigns.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Audits, keyword research, on-page optimization, content briefs, link outreach, reporting, and strategy calls.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present the SEO roadmap and content strategy upfront. Ongoing optimizations happen within sprint scope with monthly reviews.' },
      { id: 'faq-4', question: 'What if I need content written?', answer: 'Content production is a separate module. We provide briefs; you can write in-house or we can produce for an additional scope.' },
      { id: 'faq-5', question: 'Do you guarantee rankings?', answer: 'No. We guarantee best practices and execution quality. Rankings depend on competition, content quality, and algorithm changes.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'SEO tools (Ahrefs, SEMrush) if not in your stack, and potentially content writers or link building services.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with developers, content teams, and other agencies. Clear handoffs and documentation make it work.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['content', 'web-design', 'geo-aeo']
  },
  {
    id: 'affiliate-marketing',
    slug: 'affiliate-marketing',
    name: 'Affiliate Marketing',
    category: 'Digital Marketing',
    categoryId: 'digital-marketing',
    icon: 'UserGroupIcon',
    outcomeHeadline: 'Scale revenue through performance partnerships',
    subhead: 'Affiliate program setup, partner recruitment, and management that drives incremental sales.',
    typicalHours: '20-30h',
    bestFor: [
      'E-commerce brands with proven unit economics',
      'SaaS companies with recurring revenue models',
      'Businesses with affiliate-friendly margins'
    ],
    notIdealFor: [
      'Low-margin products',
      'Brands without tracking infrastructure'
    ],
    prerequisites: [
      'Affiliate tracking platform (or willingness to set up)',
      'Marketing assets (banners, copy, product info)',
      'Clear commission structure'
    ],
    whatYouGet: [
      'Performance-based revenue channel',
      'Network of partners promoting your brand',
      'Incremental sales with predictable CAC'
    ],
    deliverables: [
      'Affiliate program strategy and commission structure',
      'Tracking platform setup (ShareASale, Impact, etc.)',
      'Affiliate recruitment and outreach',
      'Marketing asset creation (banners, copy, links)',
      'Partner onboarding and training',
      'Performance monitoring and optimization',
      'Monthly reporting and partner communication',
      'Relationship management and growth'
    ],
    modules: [
      { name: 'Creative Asset Pack', description: 'Banner ads, email templates, and promo materials for affiliates', typicalHours: '10-15h' },
      { name: 'Influencer Outreach', description: 'Targeted outreach to influencers and content creators', typicalHours: '15-25h' },
      { name: 'Affiliate Event Sponsorship', description: 'Conference or event sponsorship for partner recruitment', typicalHours: '20-30h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Program Design', description: 'Commission structure, tracking setup, and affiliate portal configuration' },
      { week: 2, title: 'Recruitment', description: 'Partner outreach, application review, and onboarding of initial affiliates' },
      { week: 3, title: 'Activation', description: 'Asset distribution, training, and launch support for active partners' },
      { week: 4, title: 'Optimization', description: 'Performance analysis, top partner engagement, and recruitment expansion' }
    ],
    testimonial: {
      quote: 'Daniel was probably one of the most successful performance marketers I have ever worked with, and I have worked with +20.000 of them. Daniel quickly identified a niche in a very competitive market place and became the best in that niche.',
      name: 'Edward Ihre',
      title: 'Owner & CEO',
      company: 'Ihre Consulting'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Affiliate program setup and management fits within Growth Partner (higher capacity) due to ongoing partner communication.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Program design, tracking setup, partner recruitment, asset creation, onboarding, reporting, and relationship management.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present commission structure and program terms for approval. Ongoing partner recruitment and management happen within sprint scope.' },
      { id: 'faq-4', question: 'What if I need a large partner event?', answer: 'Conference sponsorships or large-scale events are scoped separately as project add-ons.' },
      { id: 'faq-5', question: 'Do you guarantee affiliate sales?', answer: 'No. We guarantee program setup and partner recruitment. Sales depend on offer, commission, and partner quality.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Affiliate tracking platform fees (e.g., ShareASale, Impact), commission payouts, and any event sponsorships.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We integrate with your sales, marketing, and finance teams for seamless partner management.' }
    ],
    capacityFit: 'heavy',
    relatedServices: ['paid-social', 'content', 'public-relations']
  },
  {
    id: 'media-buying',
    slug: 'media-buying',
    name: 'Media Buying',
    category: 'Digital Marketing',
    categoryId: 'digital-marketing',
    icon: 'TvIcon',
    outcomeHeadline: 'Strategic media planning that maximizes reach and ROI',
    subhead: 'Cross-channel media buying across digital, traditional, and emerging platforms.',
    typicalHours: '15-25h',
    bestFor: [
      'Brands with significant ad budgets ($50k+/month)',
      'Multi-channel campaigns requiring coordination',
      'Companies seeking strategic media planning'
    ],
    notIdealFor: [
      'Small budgets (<$10k/month)',
      'Single-channel campaigns'
    ],
    prerequisites: [
      'Clear campaign objectives and KPIs',
      'Budget allocation and approval process',
      'Creative assets or willingness to produce'
    ],
    whatYouGet: [
      'Strategic media mix recommendations',
      'Negotiated rates and placements',
      'Coordinated multi-channel execution'
    ],
    deliverables: [
      'Audience analysis and media planning',
      'Channel recommendations and budget allocation',
      'Media buying and negotiation',
      'Placement execution across channels',
      'Campaign trafficking and monitoring',
      'Performance tracking and optimization',
      'Monthly reporting with insights',
      'Post-campaign analysis and learnings'
    ],
    modules: [
      { name: 'Creative Production', description: 'Ad creative for multiple channels and formats', typicalHours: '30-50h' },
      { name: 'Programmatic Setup', description: 'DSP setup and programmatic buying infrastructure', typicalHours: '15-25h' },
      { name: 'OOH Campaign', description: 'Out-of-home advertising planning and execution', typicalHours: '25-40h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Planning & Strategy', description: 'Audience research, channel selection, budget allocation, and media plan creation' },
      { week: 2, title: 'Buying & Negotiation', description: 'Media buying, rate negotiation, placement booking, and contract execution' },
      { week: 3, title: 'Launch & Trafficking', description: 'Creative trafficking, campaign launch, and initial performance monitoring' },
      { week: 4, title: 'Optimization & Reporting', description: 'Performance analysis, budget reallocation, and campaign optimization' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Media buying is sprint-based and typically fits within Growth Partner due to coordination complexity.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Media planning, buying, negotiation, trafficking, monitoring, reporting, and client communication. Media spend is separate.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present media plan and budget allocation for approval before buying. Ongoing optimizations happen within sprint scope.' },
      { id: 'faq-4', question: 'What if I need creative production?', answer: 'Creative is scoped separately as a module. We can produce in-house or coordinate with your creative team.' },
      { id: 'faq-5', question: 'Do you guarantee media performance?', answer: 'We guarantee strategic planning and execution quality, not specific performance metrics. Results depend on creative, offer, and market.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Media spend (your budget), ad serving fees, and any platform or DSP costs.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We coordinate with internal teams, creative agencies, and media partners for seamless execution.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['paid-search', 'paid-social', 'out-of-home-marketing']
  },
  {
    id: 'content',
    slug: 'content',
    name: 'Content',
    category: 'Content & Creative',
    categoryId: 'content-creative',
    icon: 'DocumentTextIcon',
    outcomeHeadline: 'Consistent, high-quality content that drives traffic and authority',
    subhead: 'Strategic content creation including blogs, case studies, whitepapers, and website copy.',
    typicalHours: '8-15h',
    bestFor: [
      'Businesses building organic traffic',
      'B2B companies establishing thought leadership',
      'Brands needing consistent content output'
    ],
    notIdealFor: [
      'Companies without content strategy',
      'Brands expecting viral content overnight'
    ],
    prerequisites: [
      'Website or blog platform',
      'Brand voice guidelines (or willingness to develop)',
      'Subject matter expertise or access to SMEs'
    ],
    whatYouGet: [
      'SEO-optimized content that ranks',
      'Consistent publishing schedule',
      'Authority and trust building'
    ],
    deliverables: [
      'Content strategy and editorial calendar',
      'Keyword research and topic ideation',
      'Blog posts, articles, and long-form content',
      'Case studies and whitepapers',
      'Website copy and landing page content',
      'SEO optimization (meta tags, headers, internal links)',
      'Content editing and proofreading',
      'Performance tracking and optimization'
    ],
    modules: [
      { name: 'Content Audit', description: 'Comprehensive audit of existing content with optimization recommendations', typicalHours: '10-15h' },
      { name: 'Pillar Content Creation', description: 'Long-form pillar content (3,000+ words) with supporting cluster content', typicalHours: '20-30h' },
      { name: 'Video Script Writing', description: 'Scripts for video content and multimedia projects', typicalHours: '8-12h' },
      { name: 'AI Image Creation', description: 'AI-generated visuals and graphics to accompany content pieces and campaigns', typicalHours: '4-8h' },
      { name: 'AI Video Creation', description: 'AI-assisted video production for content marketing — explainers, social clips, and branded shorts', typicalHours: '6-12h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Strategy & Planning', description: 'Content audit, keyword research, editorial calendar creation, and topic prioritization' },
      { week: 2, title: 'Content Production', description: 'Writing, editing, and SEO optimization of content pieces' },
      { week: 3, title: 'Publishing & Distribution', description: 'Content publishing, internal linking, and social distribution' },
      { week: 4, title: 'Performance & Iteration', description: 'Traffic analysis, engagement tracking, and next sprint planning' }
    ],
    testimonial: {
      quote: 'Daniel has been producing phenomenal gaming websites for a long time, with some of the best poker related content available online. He has grown his portfolio of sites and positioned himself as one of the leading poker affiliates and consultants in the game.',
      name: 'Tom Galanis',
      title: 'Director',
      company: 'GameOn'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Content production is sprint-based. Typical engagement is Execution Coverage (40h/mo) for 4-6 pieces per month.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Research, writing, editing, SEO optimization, publishing, and performance tracking. Revisions are included within scope.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present editorial calendar for approval. Individual pieces go through draft review before publishing.' },
      { id: 'faq-4', question: 'What if I need a large content project?', answer: 'Pillar content, whitepapers, or ebooks are scoped separately as modules due to depth and research required.' },
      { id: 'faq-5', question: 'Do you guarantee traffic?', answer: 'No. We guarantee quality writing and SEO best practices. Traffic depends on topic selection, competition, and time.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Stock images, premium research tools, or subject matter expert interviews if needed.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with internal writers, editors, and subject matter experts for seamless content production.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['seo', 'social-media', 'strategy']
  },
  {
    id: 'photo-video',
    slug: 'photo-video',
    name: 'Photo & Video',
    category: 'Content & Creative',
    categoryId: 'content-creative',
    icon: 'FilmIcon',
    outcomeHeadline: 'Professional visual content that stops the scroll',
    subhead: 'Photography and videography from concept to final delivery, shot in Las Vegas or on location.',
    typicalHours: '25-50h',
    bestFor: [
      'Brands needing high-quality visual assets',
      'E-commerce companies with product catalogs',
      'Service businesses showcasing their work'
    ],
    notIdealFor: [
      'Brands with extremely limited budgets',
      'Projects requiring international travel'
    ],
    prerequisites: [
      'Clear creative brief and shot list',
      'Product samples or location access',
      'Timeline for production and delivery'
    ],
    whatYouGet: [
      'Professional photo and video assets',
      'Content ready for ads, social, and website',
      'Full production support from concept to delivery'
    ],
    deliverables: [
      'Concept development and creative brief',
      'Shot list and storyboard creation',
      'Professional photography or videography',
      'Lighting, styling, and art direction',
      'Editing, color grading, and post-production',
      'Multiple formats for different platforms',
      'Raw files and final deliverables',
      'Usage rights and licensing'
    ],
    modules: [
      { name: 'Full-Day Shoot', description: 'Full-day production with crew, equipment, and location', typicalHours: '40-60h' },
      { name: 'Motion Graphics', description: 'Animated graphics and text overlays for video content', typicalHours: '15-25h' },
      { name: 'Drone Footage', description: 'Aerial photography and videography', typicalHours: '10-15h' },
      { name: 'AI Video Creation', description: 'AI-assisted video production for social clips, product demos, and branded content — faster turnaround, lower cost', typicalHours: '6-12h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Pre-Production', description: 'Creative brief, shot list, location scouting, talent booking, and production planning' },
      { week: 2, title: 'Production', description: 'On-site shooting with full crew, lighting, and art direction' },
      { week: 3, title: 'Post-Production', description: 'Editing, color grading, sound design, and revisions' },
      { week: 4, title: 'Delivery & Distribution', description: 'Final deliverables, multiple formats, and asset organization' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Photo/video projects are scoped as separate modules due to production complexity. Typical range: 25-50h for half-day shoots, 40-80h for full-day productions.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Pre-production planning, shooting time, editing, revisions, and delivery. Travel time for local shoots is included.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present creative brief and shot list for approval before production. Rough cuts are reviewed before final delivery.' },
      { id: 'faq-4', question: 'What if I need multiple shoot days?', answer: 'Multi-day shoots are scoped separately. We plan these in advance and allocate capacity across sprints.' },
      { id: 'faq-5', question: 'Do you guarantee specific shots?', answer: 'We guarantee professional execution and shot list coverage. Creative outcomes depend on collaboration and feedback.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Talent fees, location rentals, props, wardrobe, travel (if outside Las Vegas), and any specialized equipment.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with creative directors, stylists, and other production partners for seamless execution.' }
    ],
    capacityFit: 'heavy',
    relatedServices: ['branding-design', 'paid-social', 'content']
  },
  {
    id: 'branding-design',
    slug: 'branding-design',
    name: 'Branding & Design',
    category: 'Content & Creative',
    categoryId: 'content-creative',
    icon: 'PaintBrushIcon',
    outcomeHeadline: 'Stand out with a brand identity that converts',
    subhead: 'Brand strategy, visual identity, and design systems that differentiate your business.',
    typicalHours: '30-50h',
    bestFor: [
      'New businesses launching their brand',
      'Established companies rebranding',
      'Brands with inconsistent visual identity'
    ],
    notIdealFor: [
      'Businesses needing quick logo tweaks',
      'Brands without strategic clarity'
    ],
    prerequisites: [
      'Clear business positioning and target audience',
      'Competitive landscape understanding',
      'Stakeholder alignment on brand direction'
    ],
    whatYouGet: [
      'Complete brand identity system',
      'Professional design assets ready to use',
      'Clear brand guidelines for consistency'
    ],
    deliverables: [
      'Brand strategy and positioning',
      'Logo design and variations',
      'Color palette and typography system',
      'Visual identity guidelines',
      'Marketing collateral templates',
      'Social media brand assets',
      'Brand voice and messaging framework',
      'Final files in all formats'
    ],
    modules: [
      { name: 'Brand Messaging Workshop', description: 'Facilitated workshop to define brand voice, messaging, and positioning', typicalHours: '10-15h' },
      { name: 'Website Design System', description: 'UI design system for website implementation', typicalHours: '25-40h' },
      { name: 'Print Collateral', description: 'Business cards, brochures, and print materials', typicalHours: '15-25h' },
      { name: 'AI Image Creation', description: 'AI-generated brand visuals, concept art, and campaign imagery to accelerate creative output', typicalHours: '4-8h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Strategy', description: 'Brand audit, competitive analysis, positioning workshop, and strategic direction' },
      { week: 2, title: 'Concept Development', description: 'Logo concepts, color exploration, typography selection, and initial design directions' },
      { week: 3, title: 'Refinement & System', description: 'Design refinement, identity system development, and collateral creation' },
      { week: 4, title: 'Guidelines & Delivery', description: 'Brand guidelines documentation, final files, and handoff' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Branding projects are scoped as separate modules due to strategic depth. Typical range: 30-50h for full brand identity.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Strategy, research, design, revisions, guidelines creation, and final file preparation.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present brand strategy for approval, then design concepts. Revisions are included within scope (typically 2-3 rounds).' },
      { id: 'faq-4', question: 'What if I need website design too?', answer: 'Website design is a separate module. We can package branding + website design for comprehensive brand launch.' },
      { id: 'faq-5', question: 'Do you guarantee I\'ll love the design?', answer: 'We guarantee professional execution and strategic alignment. Design is collaborative and requires feedback and iteration.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Premium fonts (if needed), stock imagery, and trademark filing fees if you want to protect your brand.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with stakeholders, marketing teams, and other agencies for seamless brand development.' }
    ],
    capacityFit: 'heavy',
    relatedServices: ['web-design', 'photo-video', 'strategy']
  },
  {
    id: 'web-design',
    slug: 'web-design',
    name: 'Web Design',
    category: 'Content & Creative',
    categoryId: 'content-creative',
    icon: 'GlobeAltIcon',
    outcomeHeadline: 'Convert visitors with high-performance websites',
    subhead: 'Custom website design and development from landing pages to full site builds.',
    typicalHours: '40-80h',
    bestFor: [
      'Businesses launching new websites',
      'Companies with outdated or underperforming sites',
      'Brands needing conversion-focused design'
    ],
    notIdealFor: [
      'Quick fixes or minor updates',
      'Businesses without clear content strategy'
    ],
    prerequisites: [
      'Website content or willingness to develop',
      'Brand assets (logo, colors, fonts)',
      'Hosting and domain access'
    ],
    whatYouGet: [
      'Custom website design optimized for conversions',
      'Responsive development for all devices',
      'CMS integration for easy updates'
    ],
    deliverables: [
      'UX/UI design and wireframes',
      'Custom website design (desktop and mobile)',
      'Responsive development',
      'CMS integration (WordPress, Webflow, etc.)',
      'SEO optimization and technical setup',
      'Performance optimization',
      'Launch support and testing',
      'Training and documentation'
    ],
    modules: [
      { name: 'E-commerce Integration', description: 'Shopify or WooCommerce setup with product catalog', typicalHours: '30-50h' },
      { name: 'Custom Functionality', description: 'Custom features, integrations, or interactive elements', typicalHours: '20-40h' },
      { name: 'Content Migration', description: 'Migration of existing content from old site to new', typicalHours: '15-25h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Wireframes', description: 'Site architecture, user flow mapping, wireframe creation, and content planning' },
      { week: 2, title: 'Design', description: 'Visual design, mockups, and design system development' },
      { week: 3, title: 'Development', description: 'Front-end development, CMS integration, and functionality build' },
      { week: 4, title: 'Testing & Launch', description: 'QA testing, performance optimization, launch, and training' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Website projects are scoped as separate modules. Typical range: 40-80h for landing pages, 80-150h for full site builds.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'UX/UI design, development, CMS setup, testing, revisions, and launch support. Content writing is separate.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present wireframes, then design mockups for approval. Development begins after design sign-off. Revisions included (2-3 rounds).' },
      { id: 'faq-4', question: 'What if I need e-commerce functionality?', answer: 'E-commerce is scoped separately as a module. We can package website + e-commerce for full online store.' },
      { id: 'faq-5', question: 'Do you guarantee conversions?', answer: 'We guarantee conversion-focused design and best practices. Actual conversions depend on traffic, offer, and market fit.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Hosting, domain, premium plugins/themes, stock imagery, and any third-party integrations (e.g., payment processors).' },
      { id: 'faq-7', question: 'Can you work with my existing designer?', answer: 'Yes. We can coordinate production for designs created by your team or other agencies. We ensure files are print-ready and manage the production process.' }
    ],
    capacityFit: 'heavy',
    relatedServices: ['branding-design', 'paid-search', 'seo']
  },
  {
    id: 'strategy',
    slug: 'strategy',
    name: 'Strategy',
    category: 'Strategy & Management',
    categoryId: 'strategy-management',
    icon: 'LightBulbIcon',
    outcomeHeadline: 'Get strategic clarity before you execute',
    subhead: 'Comprehensive marketing strategy with channel planning, budget allocation, and 90-day roadmap.',
    typicalHours: '20-35h',
    bestFor: [
      'Companies entering new markets',
      'Businesses without clear marketing direction',
      'Teams needing strategic alignment'
    ],
    notIdealFor: [
      'Businesses needing immediate execution',
      'Companies with clear strategy already in place'
    ],
    prerequisites: [
      'Business goals and KPIs defined',
      'Budget range for marketing execution',
      'Stakeholder availability for workshops'
    ],
    whatYouGet: [
      'Clear marketing strategy and roadmap',
      'Channel prioritization and budget allocation',
      'Actionable 90-day execution plan'
    ],
    deliverables: [
      'Market analysis and competitive research',
      'Target audience definition and personas',
      'Channel strategy and prioritization',
      'Budget allocation recommendations',
      '90-day execution roadmap',
      'KPI framework and measurement plan',
      'Messaging and positioning framework',
      'Executive presentation and handoff'
    ],
    modules: [
      { name: 'Brand Positioning Workshop', description: 'Facilitated workshop to define unique positioning and messaging', typicalHours: '8-12h' },
      { name: 'Customer Research', description: 'Customer interviews and surveys for deeper insights', typicalHours: '15-25h' },
      { name: 'Competitive Intelligence', description: 'Deep competitive analysis and market mapping', typicalHours: '12-20h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Research', description: 'Market analysis, competitive research, customer insights, and data gathering' },
      { week: 2, title: 'Strategy Development', description: 'Channel prioritization, budget modeling, messaging framework, and roadmap creation' },
      { week: 3, title: 'Refinement & Validation', description: 'Strategy refinement, stakeholder feedback, and plan optimization' },
      { week: 4, title: 'Presentation & Handoff', description: 'Executive presentation, documentation delivery, and execution planning' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Strategy projects are scoped as separate engagements. Typical range: 20-35h for comprehensive marketing strategy.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Research, analysis, workshops, strategy development, documentation, and presentation.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present initial findings for feedback, then final strategy for approval. Revisions included within scope.' },
      { id: 'faq-4', question: 'What if I need help executing the strategy?', answer: 'Execution is separate. After strategy, you can transition to Execution Coverage or Growth Partner for implementation.' },
      { id: 'faq-5', question: 'Do you guarantee results from the strategy?', answer: 'We guarantee strategic rigor and best practices. Results depend on execution quality and market conditions.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Research tools, customer survey platforms, or competitive intelligence services if needed.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with internal teams and stakeholders throughout the strategy development process.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['branding-design', 'paid-search', 'seo']
  },
  {
    id: 'social-media',
    slug: 'social-media',
    name: 'Social Media Management',
    category: 'Strategy & Management',
    categoryId: 'strategy-management',
    icon: 'ChatBubbleLeftRightIcon',
    outcomeHeadline: 'Turn your personal brand into a lead-generating machine',
    subhead: 'We manage every layer of your social media — from content strategy and video direction to caption writing, comment responses, DM automations, and lead magnets — so your brand grows while you focus on your craft.',
    typicalHours: '20-35h',
    bestFor: [
      'Chefs, personal trainers, coaches, and creators building a personal brand',
      'Stock traders, consultants, and experts monetizing their audience',
      'Founders who want a professional social presence without managing it themselves',
      'Businesses ready to convert followers into booked clients'
    ],
    notIdealFor: [
      'Brands expecting viral growth without consistent content',
      'Businesses unwilling to participate in video content creation',
      'Accounts with no defined niche or target audience'
    ],
    prerequisites: [
      'Active social media accounts (Instagram, TikTok, YouTube Shorts, or LinkedIn)',
      'Willingness to film raw video content with our direction',
      'Clear personal brand identity or openness to brand strategy session'
    ],
    whatYouGet: [
      'A fully managed social presence that attracts and converts your ideal audience',
      'Professional captions, hashtags, and posting schedule handled for you',
      'DM automations and lead magnets that turn engagement into booked calls or sales',
      'Ongoing content direction so your videos improve every sprint'
    ],
    deliverables: [
      'Personal brand page audit and strategy document',
      'Content direction (video angles, hooks, editing guidance, and format recommendations)',
      'Caption writing for every post (optimized for reach and conversion)',
      'Comment management and community engagement',
      'DM automation setup (ManyChat or equivalent)',
      'Lead magnet creation and funnel integration',
      'Content calendar and posting schedule',
      'Monthly analytics report with growth and engagement insights'
    ],
    modules: [
      { name: 'Brand Audit & Strategy', description: 'Deep-dive review of your current profile, positioning, and content gaps with a 90-day growth roadmap', typicalHours: '8-12h' },
      { name: 'DM Automation Setup', description: 'Full ManyChat or equivalent automation build — keyword triggers, welcome flows, and lead capture sequences', typicalHours: '10-16h' },
      { name: 'Lead Magnet Build', description: 'Design and deploy a lead magnet (checklist, guide, or mini-course) with opt-in funnel integration', typicalHours: '12-20h' },
      { name: 'AI Image Creation', description: 'AI-generated visuals for social posts, story graphics, and campaign imagery — fast, on-brand, scroll-stopping', typicalHours: '4-8h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Audit & Strategy', description: 'Full profile audit, competitor analysis, content pillars definition, and monthly content calendar creation' },
      { week: 2, title: 'Content Direction & Production', description: 'Video angle briefs, hook scripts, editing feedback, caption writing, and first batch of posts scheduled' },
      { week: 3, title: 'Automation & Engagement', description: 'DM automation flows live, comment responses active, lead magnet deployed, and community engagement underway' },
      { week: 4, title: 'Optimize & Report', description: 'Performance review, top-performing content analysis, strategy refinements, and next sprint planning' }
    ],
    testimonial: {
      quote: 'They completely transformed how I show up online. My DMs are now full of qualified leads and I barely have to think about what to post — they handle everything.',
      name: 'J. Todd',
      title: 'Personal Brand Client',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do I need to film my own videos?', answer: 'Yes — authentic personal brand content performs best when it features you. We provide detailed direction on angles, hooks, lighting, and format so filming is quick and easy. You send us the raw footage and we handle the rest.' },
      { id: 'faq-2', question: 'What platforms do you manage?', answer: 'We primarily work with Instagram, TikTok, YouTube Shorts, and LinkedIn. We recommend starting with 1-2 platforms and expanding once content systems are dialed in.' },
      { id: 'faq-3', question: 'How do DM automations work?', answer: 'We set up keyword-triggered flows (e.g., someone comments "guide" and automatically receives a DM with your lead magnet). These run 24/7 and can be connected to your booking system or email list.' },
      { id: 'faq-4', question: 'What is a lead magnet?', answer: 'A lead magnet is a free resource (checklist, PDF guide, mini-course, or template) that you offer in exchange for an email or phone number. We create it, design it, and connect it to your automation flows.' },
      { id: 'faq-5', question: 'Do you write all the captions?', answer: 'Yes. We write every caption in your brand voice, optimized for the platform algorithm and your audience. You review and approve before anything goes live.' },
      { id: 'faq-6', question: 'How do approvals work?', answer: 'We batch content for review weekly or bi-weekly via your client portal. You can approve, request edits, or leave notes — we handle revisions within the sprint.' },
      { id: 'faq-7', question: 'What third-party costs should I expect?', answer: 'ManyChat subscription (~$15-$45/month), any paid scheduling tools, and optional paid promotion budgets. We recommend tools based on your platform and goals.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['content', 'paid-social', 'photo-video']
  },
  {
    id: 'lifecycle-marketing',
    slug: 'lifecycle-marketing',
    name: 'Lifecycle Marketing',
    category: 'Strategy & Management',
    categoryId: 'strategy-management',
    icon: 'ArrowPathIcon',
    outcomeHeadline: 'Maximize customer lifetime value with automated journeys',
    subhead: 'Email automation, retention campaigns, and customer journey optimization.',
    typicalHours: '18-30h',
    bestFor: [
      'Subscription businesses focused on retention',
      'E-commerce brands with repeat customers',
      'SaaS companies optimizing onboarding'
    ],
    notIdealFor: [
      'One-time purchase businesses',
      'Brands without email list'
    ],
    prerequisites: [
      'Email marketing platform (Klaviyo, HubSpot, etc.)',
      'Customer data and segmentation',
      'Clear customer lifecycle stages'
    ],
    whatYouGet: [
      'Automated customer journeys',
      'Increased retention and LTV',
      'Data-driven lifecycle optimization'
    ],
    deliverables: [
      'Customer lifecycle mapping',
      'Email automation strategy and flows',
      'Segmentation and personalization setup',
      'Campaign creation (welcome, onboarding, retention)',
      'Loyalty program design and implementation',
      'Churn prevention campaigns',
      'A/B testing and optimization',
      'Monthly performance reporting'
    ],
    modules: [
      { name: 'Customer Journey Workshop', description: 'Facilitated workshop to map customer lifecycle and touchpoints', typicalHours: '8-12h' },
      { name: 'Loyalty Program Build', description: 'Custom loyalty program design and implementation', typicalHours: '25-40h' },
      { name: 'Advanced Segmentation', description: 'Predictive segmentation and behavioral triggers', typicalHours: '15-25h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Lifecycle Mapping', description: 'Customer journey mapping, segmentation strategy, and automation planning' },
      { week: 2, title: 'Flow Build', description: 'Email flow creation, copywriting, design, and automation setup' },
      { week: 3, title: 'Testing & Launch', description: 'A/B testing, flow activation, and initial monitoring' },
      { week: 4, title: 'Optimization', description: 'Performance analysis, flow optimization, and expansion planning' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Lifecycle marketing is sprint-based. Typical engagement is Execution Coverage (40h/mo) or Growth Partner for complex programs.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Strategy, flow design, copywriting, automation setup, testing, optimization, and reporting.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present lifecycle strategy and flow designs for approval. Ongoing optimizations happen within sprint scope.' },
      { id: 'faq-4', question: 'What if I need a loyalty program built?', answer: 'Loyalty programs are scoped separately due to design and technical complexity.' },
      { id: 'faq-5', question: 'Do you guarantee retention improvements?', answer: 'We guarantee best practices and automation quality. Retention depends on product, pricing, and customer experience.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Email marketing platform fees (Klaviyo, HubSpot), SMS costs if applicable, and loyalty program software.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We integrate with CRM, product, and customer success teams for seamless lifecycle optimization.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['strategy', 'content', 'paid-social']
  },
  {
    id: 'public-relations',
    slug: 'public-relations',
    name: 'Public Relations',
    category: 'Strategy & Management',
    categoryId: 'strategy-management',
    icon: 'NewspaperIcon',
    outcomeHeadline: 'Build brand authority through strategic media coverage',
    subhead: 'Media relations, press releases, and reputation management that amplifies your brand.',
    typicalHours: '15-25h',
    bestFor: [
      'Companies launching products or services',
      'Brands seeking media coverage and credibility',
      'Businesses managing reputation'
    ],
    notIdealFor: [
      'Brands not selling on Amazon',
      'Products with low margins'
    ],
    prerequisites: [
      'Amazon Seller Central or Vendor Central access',
      'Product catalog and inventory',
      'Brand Registry (recommended)'
    ],
    whatYouGet: [
      'Optimized Amazon listings',
      'Managed Amazon Ads campaigns',
      'Increased visibility and sales'
    ],
    deliverables: [
      'Listing optimization (titles, bullets, descriptions)',
      'A+ Content creation',
      'Amazon Ads setup and management',
      'Keyword research and targeting',
      'Review strategy and management',
      'Inventory and pricing recommendations',
      'Competitor analysis',
      'Monthly performance reporting'
    ],
    modules: [
      { name: 'Amazon Brand Store', description: 'Custom Amazon Brand Store design and build', typicalHours: '15-25h' },
      { name: 'Product Photography', description: 'Amazon-optimized product photography and infographics', typicalHours: '20-30h' },
      { name: 'Amazon SEO Audit', description: 'Comprehensive audit of listings with optimization roadmap', typicalHours: '10-15h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Audit & Strategy', description: 'Listing audit, keyword research, competitor analysis, and optimization planning' },
      { week: 2, title: 'Listing Optimization', description: 'Title, bullet, and description optimization, A+ Content creation, and image updates' },
      { week: 3, title: 'Ads Launch', description: 'Amazon Ads campaign setup, keyword targeting, and initial launch' },
      { week: 4, title: 'Optimization & Reporting', description: 'Bid optimization, keyword refinement, and performance reporting' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Amazon services are sprint-based. Typical engagement is Execution Coverage (40h/mo) or Growth Partner for multi-product catalogs.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Listing optimization, A+ Content creation, ads management, keyword research, reporting, and strategy.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present listing optimizations and ad strategy for approval. Ongoing optimizations happen within sprint scope.' },
      { id: 'faq-4', question: 'What if I need product photography?', answer: 'Product photography is scoped separately as a module. We can shoot Amazon-optimized images in our Las Vegas studio.' },
      { id: 'faq-5', question: 'Do you guarantee sales increases?', answer: 'We guarantee optimization best practices and ad management. Sales depend on product, pricing, competition, and reviews.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Amazon Ads spend (your budget), Amazon Seller/Vendor fees, and any premium tools (e.g., Helium 10) if needed.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We integrate with your operations, inventory, and fulfillment teams for seamless Amazon management.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['photo-video', 'paid-search', 'content']
  },
  {
    id: 'out-of-home-marketing',
    slug: 'out-of-home-marketing',
    name: 'Out of Home Marketing',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'TvIcon',
    outcomeHeadline: 'Dominate local markets with high-visibility outdoor advertising',
    subhead: 'Billboard design, placement, and outdoor advertising campaigns that drive brand awareness.',
    typicalHours: '25-40h',
    bestFor: [
      'Local businesses targeting specific markets',
      'Brands launching in new cities',
      'Companies seeking mass awareness'
    ],
    notIdealFor: [
      'Online-only businesses',
      'Brands with limited budgets (<$10k)'
    ],
    prerequisites: [
      'Clear campaign objectives and target locations',
      'Budget for media buying and creative',
      'Timeline for campaign planning and execution'
    ],
    whatYouGet: [
      'High-visibility outdoor advertising',
      'Strategic placement in target markets',
      'Mass brand awareness and reach'
    ],
    deliverables: [
      'OOH campaign strategy and planning',
      'Location research and recommendations',
      'Billboard and outdoor ad design',
      'Media buying and placement negotiation',
      'Campaign trafficking and installation',
      'Performance tracking (where applicable)',
      'Campaign photography and documentation',
      'Post-campaign analysis'
    ],
    modules: [
      { name: 'Multi-Market Campaign', description: 'OOH campaign across multiple cities or regions', typicalHours: '40-60h' },
      { name: 'Transit Advertising', description: 'Bus, subway, and transit advertising design and placement', typicalHours: '20-35h' },
      { name: 'Experiential Activation', description: 'Street-level experiential marketing and activations', typicalHours: '30-50h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Planning & Research', description: 'Location research, audience analysis, media planning, and budget allocation' },
      { week: 2, title: 'Creative & Buying', description: 'Billboard design, creative production, and media buying negotiation' },
      { week: 3, title: 'Trafficking & Launch', description: 'Creative trafficking, installation coordination, and campaign launch' },
      { week: 4, title: 'Monitoring & Reporting', description: 'Campaign monitoring, documentation, and performance reporting' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. OOH campaigns are scoped as separate projects. Typical range: 25-40h for single-market campaigns, more for multi-market.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Strategy, location research, design, media buying, trafficking, installation coordination, and reporting. Media spend is separate.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present campaign strategy and creative for approval before media buying. Installation is coordinated with your timeline.' },
      { id: 'faq-4', question: 'What if I need a multi-city campaign?', answer: 'Multi-market campaigns are scoped separately based on number of locations and complexity.' },
      { id: 'faq-5', question: 'Do you guarantee impressions?', answer: 'We guarantee strategic placement and execution. Impressions depend on location, traffic, and campaign duration.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Billboard rental fees, installation costs, printing, and any permits or regulatory fees.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with creative teams, media buyers, and local partners for seamless OOH execution.' }
    ],
    capacityFit: 'heavy',
    relatedServices: ['branding-design', 'media-buying', 'photo-video']
  },
  {
    id: 'geo-aeo',
    slug: 'geo-aeo',
    name: 'GEO | AEO',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'SparklesIcon',
    outcomeHeadline: 'Optimize for AI-powered search engines and voice assistants',
    subhead: 'Generative Engine Optimization (GEO) and Answer Engine Optimization (AEO) for ChatGPT, Perplexity, and Google SGE.',
    typicalHours: '12-20h',
    bestFor: [
      'Forward-thinking companies preparing for AI search',
      'Content-heavy sites wanting AI visibility',
      'Brands targeting voice search'
    ],
    notIdealFor: [
      'Businesses without existing content',
      'Companies focused only on traditional SEO'
    ],
    prerequisites: [
      'Website with content',
      'Structured data capability',
      'Willingness to adapt content strategy'
    ],
    whatYouGet: [
      'Optimized content for AI search engines',
      'Improved visibility in ChatGPT, Perplexity, etc.',
      'Future-proofed search strategy'
    ],
    deliverables: [
      'GEO/AEO audit and opportunity analysis',
      'Content optimization for AI engines',
      'Structured data implementation',
      'Answer-focused content creation',
      'FAQ and Q&A optimization',
      'Entity optimization and knowledge graph',
      'Voice search optimization',
      'Performance tracking and reporting'
    ],
    modules: [
      { name: 'Content Restructuring', description: 'Comprehensive content restructuring for AI search', typicalHours: '20-30h' },
      { name: 'Knowledge Graph Build', description: 'Entity optimization and knowledge graph development', typicalHours: '15-25h' },
      { name: 'Voice Search Strategy', description: 'Voice search optimization and conversational content', typicalHours: '12-20h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Audit & Strategy', description: 'GEO/AEO audit, AI search analysis, and optimization roadmap' },
      { week: 2, title: 'Content Optimization', description: 'Content restructuring, answer-focused rewrites, and FAQ optimization' },
      { week: 3, title: 'Technical Implementation', description: 'Structured data setup, schema markup, and entity optimization' },
      { week: 4, title: 'Testing & Reporting', description: 'AI search testing, performance tracking, and optimization recommendations' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. GEO/AEO work is sprint-based. Typical engagement is Execution Coverage (40h/mo) or one-time optimization project.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Audit, content optimization, structured data implementation, testing, and reporting.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present optimization strategy and content changes for approval. Technical implementation happens within sprint scope.' },
      { id: 'faq-4', question: 'What if I need new content created?', answer: 'Content creation is a separate module. We can produce answer-focused content optimized for AI search.' },
      { id: 'faq-5', question: 'Do you guarantee AI search rankings?', answer: 'No. AI search is evolving. We guarantee best practices and optimization quality based on current understanding.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Minimal. Structured data tools and testing platforms if needed.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with SEO, content, and development teams for seamless GEO/AEO implementation.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['seo', 'content', 'strategy']
  },
  {
    id: 'reputation-management',
    slug: 'reputation-management',
    name: 'Reputation Management (Birdeye)',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'StarIcon',
    outcomeHeadline: 'Build and protect your online reputation with proactive review management',
    subhead: 'Automated review generation, monitoring, and response management using Birdeye platform to strengthen trust and visibility.',
    typicalHours: '10-18h',
    bestFor: [
      'Service businesses with multiple locations',
      'Companies with high customer interaction',
      'Businesses wanting to improve online ratings'
    ],
    notIdealFor: [
      'Businesses without customer-facing operations',
      'Businesses unwilling to engage with reviews'
    ],
    prerequisites: [
      'Active business locations',
      'Customer contact database',
      'Willingness to respond to reviews regularly'
    ],
    whatYouGet: [
      'Automated review request system',
      'Centralized review monitoring dashboard',
      'Improved online reputation and ratings'
    ],
    deliverables: [
      'Birdeye platform setup and configuration',
      'Review generation automation workflows',
      'Multi-platform review monitoring (Google, Yelp, Facebook)',
      'Review response templates and guidelines',
      'Sentiment analysis and reporting dashboard',
      'Team training on platform usage',
      'Monthly reputation reports',
      'Ongoing optimization and support'
    ],
    modules: [
      { name: 'Referral Program Setup', description: 'Customer referral program with tracking and rewards', typicalHours: '12-18h' },
      { name: 'Surveys & Feedback', description: 'Customer satisfaction surveys and feedback collection', typicalHours: '8-12h' },
      { name: 'Webchat Integration', description: 'Live chat widget for website with Birdeye integration', typicalHours: '6-10h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Setup & Configuration', description: 'Birdeye account setup, location configuration, review source connections, and workflow design' },
      { week: 2, title: 'Automation Build', description: 'Review request automation, email/SMS templates, response templates, and team access setup' },
      { week: 3, title: 'Training & Launch', description: 'Team training, process documentation, initial review responses, and system testing' },
      { week: 4, title: 'Optimization', description: 'Performance monitoring, workflow refinement, reporting setup, and ongoing support' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Reputation management setup is sprint-based. Ongoing management typically fits within Foundations (20h/mo) or Execution Coverage.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Platform setup, workflow configuration, template creation, team training, review monitoring, response management, and reporting.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present automation workflows and response templates for approval before launch. Ongoing responses follow your brand guidelines.' },
      { id: 'faq-4', question: 'What if I need custom features?', answer: 'Advanced features like referral programs or webchat are scoped as separate modules and planned accordingly.' },
      { id: 'faq-5', question: 'Do you guarantee more reviews?', answer: 'We guarantee proper setup and best practices. Review volume depends on customer satisfaction and request timing.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Birdeye subscription (varies by location count and features). We help you choose the right plan for your needs.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We train your team on the platform and create processes that integrate with your existing customer service workflows.' }
    ],
    capacityFit: 'light',
    relatedServices: ['social-media', 'public-relations', 'lifecycle-marketing']
  },
  {
    id: 'premium-print-cards',
    slug: 'premium-print-cards',
    name: 'Premium Print & Cards',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'RectangleStackIcon',
    outcomeHeadline: 'Premium print materials that make lasting impressions',
    subhead: 'Professional business cards, postcards, brochures, catalogs, and specialty print products with premium finishes that reflect your brand quality.',
    typicalHours: '3-8h',
    bestFor: [
      'Businesses needing professional print collateral',
      'Brands wanting premium, tangible marketing materials',
      'Companies attending trade shows or events',
      'Service businesses requiring business cards and brochures'
    ],
    notIdealFor: [
      'Digital-only marketing strategies',
      'Businesses with no in-person customer interaction'
    ],
    prerequisites: [
      'Brand assets (logo, colors, fonts)',
      'Content and messaging ready',
      'Clear understanding of quantity needs'
    ],
    whatYouGet: [
      'Premium print materials with professional finishes',
      'Tangible marketing collateral that reflects brand quality',
      'Multiple finish and material options for differentiation'
    ],
    deliverables: [
      'Print-ready design files',
      'Material and finish recommendations',
      'Print production coordination',
      'Quality control and proofing',
      'Direct shipping to your location',
      'Multiple product options: business cards, plastic cards, postcards, hang tags, catalogs, brochures, presentation folders, stickers',
      'Premium finish options: thick cards, foil, spot UV, die-cut, colored edge, emboss, perforate, rounded corners',
      'Specialty finishes: Silk, Suede, Cotton, Linen, Wood, Metal'
    ],
    modules: [
      { name: 'Custom Design', description: 'Full custom design for print collateral from concept to final files', typicalHours: '15-25h' },
      { name: 'Large Format Printing', description: 'Posters, banners, and signage for events or retail', typicalHours: '8-15h' },
      { name: 'Packaging Design', description: 'Custom product packaging and box design with print production', typicalHours: '25-40h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Consultation & Planning', description: 'Product selection, finish recommendations, quantity planning, and design file review' },
      { week: 1, title: 'Production Setup', description: 'Print file preparation, proofing, material selection, and production coordination' },
      { week: 2, title: 'Quality Control', description: 'Print proofs review, color matching, finish verification, and approval process' },
      { week: 2, title: 'Production & Delivery', description: 'Final production run, quality inspection, and direct shipping to your location' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Print coordination is sprint-based and typically fits within Foundations (20h/mo) or as a standalone project. Complexity varies by product type and customization.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Consultation, design file preparation, finish selection, production coordination, proofing, quality control, and shipping logistics. Print production costs are separate.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present material and finish recommendations, then provide digital proofs for approval before production. You approve final proofs before printing.' },
      { id: 'faq-4', question: 'What if I need custom design work?', answer: 'Custom design is scoped as a separate module. If you have existing brand assets, we adapt them for print. Full custom design requires additional capacity.' },
      { id: 'faq-5', question: 'Do you guarantee print quality?', answer: 'Yes. We coordinate with premium print partners and conduct quality control. Any production defects are reprinted at no additional cost.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Print production costs (varies by product, quantity, and finishes), shipping fees, and rush production fees if needed. We provide detailed quotes upfront.' },
      { id: 'faq-7', question: 'Can you work with my existing designer?', answer: 'Yes. We can coordinate production for designs created by your team or other agencies. We ensure files are print-ready and manage the production process.' }
    ],
    capacityFit: 'light',
    relatedServices: ['branding-design', 'web-design', 'content']
  },
  {
    id: 'amazon-services',
    slug: 'amazon-services',
    name: 'Amazon Services',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'ShoppingBagIcon',
    outcomeHeadline: 'Maximize Amazon sales with optimized listings and ads',
    subhead: 'Complete Amazon marketplace optimization including listings, ads, and sales strategy.',
    typicalHours: '20-35h',
    bestFor: [
      'E-commerce brands selling on Amazon',
      'Product companies launching on Amazon',
      'Sellers wanting to scale Amazon revenue'
    ],
    notIdealFor: [
      'Brands not selling on Amazon',
      'Products with low margins'
    ],
    prerequisites: [
      'Amazon Seller Central or Vendor Central access',
      'Product catalog and inventory',
      'Brand Registry (recommended)'
    ],
    whatYouGet: [
      'Optimized Amazon listings',
      'Managed Amazon Ads campaigns',
      'Increased visibility and sales'
    ],
    deliverables: [
      'Listing optimization (titles, bullets, descriptions)',
      'A+ Content creation',
      'Amazon Ads setup and management',
      'Keyword research and targeting',
      'Review strategy and management',
      'Inventory and pricing recommendations',
      'Competitor analysis',
      'Monthly performance reporting'
    ],
    modules: [
      { name: 'Amazon Brand Store', description: 'Custom Amazon Brand Store design and build', typicalHours: '15-25h' },
      { name: 'Product Photography', description: 'Amazon-optimized product photography and infographics', typicalHours: '20-30h' },
      { name: 'Amazon SEO Audit', description: 'Comprehensive audit of listings with optimization roadmap', typicalHours: '10-15h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Audit & Strategy', description: 'Listing audit, keyword research, competitor analysis, and optimization planning' },
      { week: 2, title: 'Listing Optimization', description: 'Title, bullet, and description optimization, A+ Content creation, and image updates' },
      { week: 3, title: 'Ads Launch', description: 'Amazon Ads campaign setup, keyword targeting, and initial launch' },
      { week: 4, title: 'Optimization & Reporting', description: 'Bid optimization, keyword refinement, and performance reporting' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Amazon services are sprint-based. Typical engagement is Execution Coverage (40h/mo) or Growth Partner for multi-product catalogs.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Listing optimization, A+ Content creation, ads management, keyword research, reporting, and strategy.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present listing optimizations and ad strategy for approval. Ongoing optimizations happen within sprint scope.' },
      { id: 'faq-4', question: 'What if I need product photography?', answer: 'Product photography is scoped separately as a module. We can shoot Amazon-optimized images in our Las Vegas studio.' },
      { id: 'faq-5', question: 'Do you guarantee sales increases?', answer: 'We guarantee optimization best practices and ad management. Sales depend on product, pricing, competition, and reviews.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Amazon Ads spend (your budget), Amazon Seller/Vendor fees, and any premium tools (e.g., Helium 10) if needed.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We integrate with your operations, inventory, and fulfillment teams for seamless Amazon management.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['photo-video', 'paid-search', 'content']
  },
  {
    id: 'out-of-home-marketing',
    slug: 'out-of-home-marketing',
    name: 'Out of Home Marketing',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'TvIcon',
    outcomeHeadline: 'Dominate local markets with high-visibility outdoor advertising',
    subhead: 'Billboard design, placement, and outdoor advertising campaigns that drive brand awareness.',
    typicalHours: '25-40h',
    bestFor: [
      'Local businesses targeting specific markets',
      'Brands launching in new cities',
      'Companies seeking mass awareness'
    ],
    notIdealFor: [
      'Online-only businesses',
      'Brands with limited budgets (<$10k)'
    ],
    prerequisites: [
      'Clear campaign objectives and target locations',
      'Budget for media buying and creative',
      'Timeline for campaign planning and execution'
    ],
    whatYouGet: [
      'High-visibility outdoor advertising',
      'Strategic placement in target markets',
      'Mass brand awareness and reach'
    ],
    deliverables: [
      'OOH campaign strategy and planning',
      'Location research and recommendations',
      'Billboard and outdoor ad design',
      'Media buying and placement negotiation',
      'Campaign trafficking and installation',
      'Performance tracking (where applicable)',
      'Campaign photography and documentation',
      'Post-campaign analysis'
    ],
    modules: [
      { name: 'Multi-Market Campaign', description: 'OOH campaign across multiple cities or regions', typicalHours: '40-60h' },
      { name: 'Transit Advertising', description: 'Bus, subway, and transit advertising design and placement', typicalHours: '20-35h' },
      { name: 'Experiential Activation', description: 'Street-level experiential marketing and activations', typicalHours: '30-50h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Planning & Research', description: 'Location research, audience analysis, media planning, and budget allocation' },
      { week: 2, title: 'Creative & Buying', description: 'Billboard design, creative production, and media buying negotiation' },
      { week: 3, title: 'Trafficking & Launch', description: 'Creative trafficking, installation coordination, and campaign launch' },
      { week: 4, title: 'Monitoring & Reporting', description: 'Campaign monitoring, documentation, and performance reporting' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. OOH campaigns are scoped as separate projects. Typical range: 25-40h for single-market campaigns, more for multi-market.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Strategy, location research, design, media buying, trafficking, installation coordination, and reporting. Media spend is separate.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present campaign strategy and creative for approval before media buying. Installation is coordinated with your timeline.' },
      { id: 'faq-4', question: 'What if I need a multi-city campaign?', answer: 'Multi-market campaigns are scoped separately based on number of locations and complexity.' },
      { id: 'faq-5', question: 'Do you guarantee impressions?', answer: 'We guarantee strategic placement and execution. Impressions depend on location, traffic, and campaign duration.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Billboard rental fees, installation costs, printing, and any permits or regulatory fees.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with creative teams, media buyers, and local partners for seamless OOH execution.' }
    ],
    capacityFit: 'heavy',
    relatedServices: ['branding-design', 'media-buying', 'photo-video']
  },
  {
    id: 'geo-aeo',
    slug: 'geo-aeo',
    name: 'GEO | AEO',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'SparklesIcon',
    outcomeHeadline: 'Optimize for AI-powered search engines and voice assistants',
    subhead: 'Generative Engine Optimization (GEO) and Answer Engine Optimization (AEO) for ChatGPT, Perplexity, and Google SGE.',
    typicalHours: '12-20h',
    bestFor: [
      'Forward-thinking companies preparing for AI search',
      'Content-heavy sites wanting AI visibility',
      'Brands targeting voice search'
    ],
    notIdealFor: [
      'Businesses without existing content',
      'Companies focused only on traditional SEO'
    ],
    prerequisites: [
      'Website with content',
      'Structured data capability',
      'Willingness to adapt content strategy'
    ],
    whatYouGet: [
      'Optimized content for AI search engines',
      'Improved visibility in ChatGPT, Perplexity, etc.',
      'Future-proofed search strategy'
    ],
    deliverables: [
      'GEO/AEO audit and opportunity analysis',
      'Content optimization for AI engines',
      'Structured data implementation',
      'Answer-focused content creation',
      'FAQ and Q&A optimization',
      'Entity optimization and knowledge graph',
      'Voice search optimization',
      'Performance tracking and reporting'
    ],
    modules: [
      { name: 'Content Restructuring', description: 'Comprehensive content restructuring for AI search', typicalHours: '20-30h' },
      { name: 'Knowledge Graph Build', description: 'Entity optimization and knowledge graph development', typicalHours: '15-25h' },
      { name: 'Voice Search Strategy', description: 'Voice search optimization and conversational content', typicalHours: '12-20h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Audit & Strategy', description: 'GEO/AEO audit, AI search analysis, and optimization roadmap' },
      { week: 2, title: 'Content Optimization', description: 'Content restructuring, answer-focused rewrites, and FAQ optimization' },
      { week: 3, title: 'Technical Implementation', description: 'Structured data setup, schema markup, and entity optimization' },
      { week: 4, title: 'Testing & Reporting', description: 'AI search testing, performance tracking, and optimization recommendations' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. GEO/AEO work is sprint-based. Typical engagement is Execution Coverage (40h/mo) or one-time optimization project.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Audit, content optimization, structured data implementation, testing, and reporting.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present optimization strategy and content changes for approval. Technical implementation happens within sprint scope.' },
      { id: 'faq-4', question: 'What if I need new content created?', answer: 'Content creation is a separate module. We can produce answer-focused content optimized for AI search.' },
      { id: 'faq-5', question: 'Do you guarantee AI search rankings?', answer: 'No. AI search is evolving. We guarantee best practices and optimization quality based on current understanding.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Minimal. Structured data tools and testing platforms if needed.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with SEO, content, and development teams for seamless GEO/AEO implementation.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['seo', 'content', 'strategy']
  },
  {
    id: 'reputation-management',
    slug: 'reputation-management',
    name: 'Reputation Management (Birdeye)',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'StarIcon',
    outcomeHeadline: 'Proactively manage reviews and online reputation',
    subhead: 'Birdeye-powered reputation management with review generation, monitoring, and response.',
    typicalHours: '10-18h',
    bestFor: [
      'Service businesses needing reviews',
      'Multi-location companies',
      'Brands managing online reputation'
    ],
    notIdealFor: [
      'Businesses without customer interactions',
      'Brands with fundamental service issues'
    ],
    prerequisites: [
      'Customer contact information',
      'Willingness to request reviews',
      'Birdeye account (or willingness to set up)'
    ],
    whatYouGet: [
      'Automated review generation',
      'Proactive reputation monitoring',
      'Professional review responses'
    ],
    deliverables: [
      'Birdeye platform setup and configuration',
      'Review generation automation',
      'Review monitoring across platforms',
      'Response templates and strategy',
      'Sentiment analysis and reporting',
      'Negative review management',
      'Reputation dashboard setup',
      'Monthly performance reporting'
    ],
    modules: [
      { name: 'Multi-Location Setup', description: 'Birdeye setup for multiple locations with centralized management', typicalHours: '15-25h' },
      { name: 'Crisis Response', description: 'Rapid response to negative reviews or reputation issues', typicalHours: '8-15h' },
      { name: 'Review Marketing', description: 'Leverage positive reviews in marketing campaigns', typicalHours: '10-15h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Setup & Strategy', description: 'Birdeye setup, review strategy, and automation configuration' },
      { week: 2, title: 'Template Creation', description: 'Response templates, review request messaging, and workflow setup' },
      { week: 3, title: 'Launch & Training', description: 'System launch, team training, and initial review generation' },
      { week: 4, title: 'Optimization', description: 'Performance monitoring, workflow refinement, reporting setup, and ongoing support' }
    ],
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Reputation management is sprint-based. Initial setup is one-time, ongoing management fits within Execution Coverage (40h/mo) or AutoPilot.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Platform setup, automation configuration, response writing, monitoring, reporting, and strategy.' },
      { id: 'faq-3', question: 'How do approvals work?', answer: 'We present review strategy and response templates for approval. Ongoing responses follow approved guidelines.' },
      { id: 'faq-4', question: 'What if I have a reputation crisis?', answer: 'Crisis response is scoped separately due to urgency and intensity. We can provide rapid response support.' },
      { id: 'faq-5', question: 'Do you guarantee 5-star reviews?', answer: 'No. We guarantee review generation and professional responses. Review quality depends on your service delivery.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Birdeye subscription fees (varies by plan and locations).' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We integrate with customer service, operations, and marketing teams for seamless reputation management.' }
    ],
    capacityFit: 'light',
    relatedServices: ['social-media', 'public-relations', 'strategy']
  },
  {
    id: 'paid-media-management',
    slug: 'paid-media-management',
    name: 'Paid Media Management',
    category: 'Digital Marketing',
    categoryId: 'digital-marketing',
    icon: 'ChartBarIcon',
    outcomeHeadline: 'Performance-driven paid media execution across all channels',
    subhead: 'Multi-channel paid advertising management across Meta, Google, TikTok, and LinkedIn with sprint-based execution and US-based team support.',
    typicalHours: '15-25h',
    bestFor: [
      'Scaling brands ready to invest in paid growth',
      'Performance-focused teams needing execution support',
      'Multi-channel campaigns requiring coordination'
    ],
    notIdealFor: [
      'Organic-only strategies without paid budget',
      'Brand awareness campaigns without conversion goals'
    ],
    prerequisites: [
      'Ad platform access (Meta Business Manager, Google Ads, etc.)',
      'Minimum $5k/month ad spend budget',
      'Landing pages or website ready for traffic'
    ],
    whatYouGet: [
      'Multi-channel campaign execution with Vegas creative energy',
      'Data-driven optimization and A/B testing',
      'Full transparency with weekly reporting'
    ],
    deliverables: [
      'Campaign strategy and media planning',
      'Ad creative production (static and video)',
      'Audience research and targeting setup',
      'Budget allocation and bid management',
      'A/B testing framework and execution',
      'Landing page optimization recommendations',
      'Weekly performance reporting',
      'Monthly strategy review and planning'
    ],
    modules: [
      { name: 'Creative Batch Production', description: 'High-volume ad creative production for testing and scaling', typicalHours: '8-12h' },
      { name: 'Landing Page Build', description: 'Custom landing page optimized for paid traffic conversion', typicalHours: '10-15h' },
      { name: 'Funnel Build', description: 'Complete funnel design and implementation from ad to conversion', typicalHours: '15-20h' },
      { name: 'Analytics Setup', description: 'Advanced tracking, attribution, and analytics infrastructure', typicalHours: '4-6h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Strategy & Setup', description: 'Campaign planning, audience research, creative brief, and account audit across all platforms' },
      { week: 2, title: 'Campaign Build', description: 'Ad creative production, campaign setup, tracking implementation, and coordinated launch' },
      { week: 3, title: 'Test & Optimize', description: 'A/B testing execution, audience refinement, budget optimization, and performance monitoring' },
      { week: 4, title: 'Scale & Report', description: 'Scale winning campaigns, comprehensive reporting, ROI analysis, and next sprint planning' }
    ],
    testimonial: {
      quote: 'The paid media ROI we achieved with Best Odds exceeded our expectations. Their sprint-based approach and weekly reporting gave us full visibility into performance while their creative energy kept our campaigns fresh.',
      name: 'Marketing Director',
      title: 'Director of Marketing',
      company: 'E-commerce Brand'
    },
    faq: [
      { id: 'faq-1', question: 'How do platform spend and management fees work?', answer: 'You pay ad spend directly to platforms (Meta, Google, TikTok, etc.). Our management fee is sprint-based capacity, not a percentage of spend. Paid Media Management typically fits Growth Partner (higher capacity) for multi-channel execution.' },
      { id: 'faq-2', question: 'What is your creative refresh cadence?', answer: 'We refresh creative every 2-4 weeks based on performance data and platform fatigue. High-performing ads stay live, underperformers get replaced. Creative Batch Production module accelerates this cycle.' },
      { id: 'faq-3', question: 'What attribution models do you use?', answer: 'We track multiple attribution models (first-click, last-click, multi-touch) and focus on the model that aligns with your business goals. We provide full transparency on platform attribution vs. actual conversions.' },
      { id: 'faq-4', question: 'Do we need to provide platform access?', answer: 'Yes. We need admin access to ad accounts (Meta Business Manager, Google Ads, TikTok Ads Manager, LinkedIn Campaign Manager). We never request payment method access.' },
      { id: 'faq-5', question: 'How do approvals work?', answer: 'We present campaign strategy and initial creative for approval before launch. Ongoing optimizations (bid adjustments, audience refinements) happen within sprint scope with weekly updates. Major creative changes get approval.' },
      { id: 'faq-6', question: 'What if I need landing pages built?', answer: 'Landing page builds are scoped as separate modules (10-15h typical). We can build custom pages optimized for your paid traffic or optimize existing pages.' },
      { id: 'faq-7', question: 'Do you guarantee specific ROAS or CPA?', answer: 'No. We guarantee execution quality, best practices, and sprint-based optimization. Performance depends on offer, creative, market conditions, and budget. We optimize toward your target metrics.' },
      { id: 'faq-8', question: 'Can you work with our existing creative team?', answer: 'Absolutely. We collaborate with internal teams, creative agencies, and freelancers. Clear briefs and feedback loops make it seamless.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['paid-search', 'paid-social', 'photo-video']
  },
  {
    id: 'growth-marketing',
    slug: 'growth-marketing',
    name: 'Growth Marketing',
    category: 'Strategy & Management',
    categoryId: 'strategy-management',
    icon: 'RocketLaunchIcon',
    outcomeHeadline: 'Build a predictable growth engine that scales revenue month over month',
    subhead: 'Full-funnel growth marketing strategy and execution — from acquisition to retention — built for e-commerce and DTC brands ready to scale.',
    typicalHours: '20-35h',
    bestFor: [
      'E-commerce and DTC brands ready to scale',
      'Businesses with proven product-market fit',
      'Companies wanting a unified growth strategy across channels'
    ],
    notIdealFor: [
      'Brands without a validated offer or product',
      'Businesses looking for a single-channel solution only'
    ],
    prerequisites: [
      'Active website with analytics tracking',
      'Existing customer base or prior sales history',
      'Willingness to invest across multiple channels'
    ],
    whatYouGet: [
      'A unified growth strategy across paid, organic, and owned channels',
      'Conversion rate improvements across the full funnel',
      'Predictable revenue growth with clear attribution'
    ],
    deliverables: [
      'Full-funnel growth audit and opportunity analysis',
      'Multi-channel strategy and execution roadmap',
      'Paid acquisition campaign setup and management',
      'Email and SMS automation sequences',
      'Conversion rate optimization (CRO) recommendations',
      'Retention and loyalty program strategy',
      'A/B testing framework and execution',
      'Monthly growth reporting with KPI dashboard'
    ],
    modules: [
      { name: 'Email & SMS Automation', description: 'Klaviyo or Attentive flows: welcome, abandoned cart, post-purchase, win-back', typicalHours: '15-25h' },
      { name: 'CRO Sprint', description: 'Landing page and checkout optimization with A/B testing', typicalHours: '12-20h' },
      { name: 'Retention Program', description: 'Loyalty, referral, and repeat-purchase strategy and setup', typicalHours: '10-18h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Growth Audit', description: 'Full-funnel audit, channel analysis, customer journey mapping, and growth opportunity identification' },
      { week: 2, title: 'Strategy & Setup', description: 'Multi-channel strategy finalization, campaign setup, automation configuration, and tracking implementation' },
      { week: 3, title: 'Launch & Test', description: 'Campaign launch, A/B test execution, email/SMS flow activation, and CRO implementation' },
      { week: 4, title: 'Optimize & Report', description: 'Performance analysis, budget reallocation, funnel optimization, and next-sprint growth planning' }
    ],
    testimonial: {
      quote: 'Best Odds built us a real growth engine — not just ads. They connected our paid channels, email flows, and retention strategy into one system that actually compounds.',
      name: 'E-commerce Founder',
      title: 'Founder & CEO',
      company: 'DTC Brand'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Growth Marketing is sprint-based. It typically fits within Growth Partner (higher capacity) due to the multi-channel scope, or Execution Coverage for focused single-channel growth work.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Strategy, campaign setup, email/SMS automation, CRO work, A/B testing, reporting, and client communication. Ad spend and platform fees are separate.' },
      { id: 'faq-3', question: 'How many SOPs can you write in one sprint?', answer: 'Typically 5-10 core processes per sprint depending on complexity. We prioritize the highest-impact processes first.' },
      { id: 'faq-4', question: 'What format are the SOPs delivered in?', answer: 'Written documents with step-by-step instructions, decision trees, and flowcharts. We can deliver in Google Docs, Notion, Trainual, or your preferred platform.' },
      { id: 'faq-5', question: 'Do you need to observe our processes?', answer: 'We conduct process mapping sessions with the people who perform the work. Observation or screen recordings are helpful but not always required.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'SOP software if you want a dedicated platform (Trainual ~$49/month, Notion free tier available). We can work in any format.' },
      { id: 'faq-7', question: 'Can you update existing SOPs?', answer: 'Yes. We can audit, update, and improve existing documentation. We will assess what you have and recommend the most efficient approach.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['paid-search', 'paid-social', 'lifecycle-marketing']
  }
];

// ─── NEW SERVICES APPENDED BELOW ───────────────────────────────────────────

const newServices: ServiceData[] = [
  // ── Content & Creative ──────────────────────────────────────────────────
  {
    id: 'pitch-deck-presentation',
    slug: 'pitch-deck-presentation',
    name: 'Pitch Deck & Presentation Creation',
    category: 'Content & Creative',
    categoryId: 'content-creative',
    icon: 'PresentationChartBarIcon',
    outcomeHeadline: 'Walk into every room with a deck that closes deals',
    subhead: 'Investor-ready pitch decks and executive presentations that combine strategic storytelling with polished design.',
    typicalHours: '15-30h',
    bestFor: [
      'Founders raising pre-seed, seed, or Series A funding',
      'Executives presenting to boards or enterprise clients',
      'Sales teams needing a high-converting leave-behind',
      'Businesses pitching partnerships or sponsorships'
    ],
    notIdealFor: [
      'Companies without a clear value proposition or business model',
      'Brands needing a simple one-pager (use Content instead)'
    ],
    prerequisites: [
      'Business overview, financials, or talking points',
      'Brand assets (logo, colors, fonts)',
      'Clear audience and purpose for the deck'
    ],
    whatYouGet: [
      'A professionally designed, narrative-driven deck ready to present',
      'Slide-by-slide messaging that builds conviction',
      'Editable source files so you can update it yourself'
    ],
    deliverables: [
      'Narrative structure and slide outline',
      'Copywriting for every slide',
      'Custom visual design (charts, icons, layouts)',
      'Data visualization and infographics',
      'Branded template for future use',
      'PowerPoint and PDF export',
      'Up to 2 rounds of revisions',
      'Presenter notes (optional)'
    ],
    modules: [
      { name: 'Financial Model Visualization', description: 'Turn your financial projections into clean, investor-friendly charts and tables', typicalHours: '6-10h' },
      { name: 'One-Pager Summary', description: 'Single-page executive summary or teaser document to accompany the deck', typicalHours: '4-6h' },
      { name: 'Pitch Coaching Session', description: 'Live coaching session to refine your delivery and anticipate investor questions', typicalHours: '3-5h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Outline', description: 'Intake call, narrative structure development, slide outline, and messaging framework' },
      { week: 2, title: 'Copy & Design', description: 'Slide copywriting, visual design, data visualization, and initial draft delivery' },
      { week: 3, title: 'Revisions & Polish', description: 'Client feedback incorporation, design refinement, and final polish' },
      { week: 4, title: 'Final Delivery', description: 'Final files in all formats, editable template, and optional presenter notes' }
    ],
    testimonial: {
      quote: 'Our pitch deck went from a wall of text to a story investors actually wanted to hear. We closed our round within 6 weeks of the new deck.',
      name: 'Pitch Deck Client',
      title: 'Founder',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Pitch deck projects are scoped upfront. Typical range: 15-30h depending on complexity, number of slides, and data visualization needs.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Discovery, narrative development, copywriting, design, revisions, and file preparation.' },
      { id: 'faq-3', question: 'How many slides is typical?', answer: 'Investor decks typically run 12-18 slides. Sales and partnership decks vary. We scope based on your specific needs.' },
      { id: 'faq-4', question: 'Do I need to provide content?', answer: 'You provide the raw information — business model, metrics, team bios, etc. We shape it into a compelling narrative and design it.' },
      { id: 'faq-5', question: 'What formats do you deliver?', answer: 'PowerPoint (.pptx), PDF, and Google Slides if requested. All files are fully editable.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Stock imagery or icon licenses if needed. Most decks use custom design elements at no additional cost.' },
      { id: 'faq-7', question: 'Can you update an existing deck?', answer: 'Yes. We can redesign or refresh an existing deck. We will assess what you have and recommend the most efficient approach.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['branding-design', 'strategy', 'content']
  },
  {
    id: 'book-writing',
    slug: 'book-writing',
    name: 'Book Writing',
    category: 'Content & Creative',
    categoryId: 'content-creative',
    icon: 'BookOpenIcon',
    outcomeHeadline: 'Publish the book that establishes you as the authority in your field',
    subhead: 'Full-service book writing from concept to manuscript — ghostwritten in your voice, structured to convert readers into clients.',
    typicalHours: '60-120h',
    bestFor: [
      'Executives and founders building thought leadership',
      'Coaches and consultants who want a high-ticket lead magnet',
      'Experts with a methodology worth sharing at scale',
      'Entrepreneurs who have the ideas but not the time to write'
    ],
    notIdealFor: [
      'Clients without a clear topic, audience, or core message',
      'Projects needing a book in under 4 weeks'
    ],
    prerequisites: [
      'Clear topic, audience, and purpose for the book',
      'Availability for 2-3 voice capture sessions',
      'Existing content, talks, or frameworks to draw from (helpful but not required)'
    ],
    whatYouGet: [
      'A complete, publish-ready manuscript in your voice',
      'Chapter-by-chapter structure that builds your authority',
      'A book that works as a business development tool'
    ],
    deliverables: [
      'Book concept, format, and branding',
      'Show name, description, and cover art',
      'Episode structure and interview framework',
      'Audio recording, editing, and mastering',
      'Show notes and episode descriptions',
      'Distribution setup (Spotify, Apple Podcasts, etc.)',
      'Audiogram and social clips for each episode',
      'Monthly performance reporting'
    ],
    modules: [
      { name: 'Podcast Launch Pack', description: 'First 3 episodes produced and published simultaneously for a strong launch', typicalHours: '25-35h' },
      { name: 'Guest Booking', description: 'Outreach and scheduling for guest interviews aligned to your audience', typicalHours: '8-12h' },
      { name: 'Video Podcast Setup', description: 'Video recording setup and YouTube distribution for visual podcast format', typicalHours: '10-15h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Concept & Branding', description: 'Podcast concept, show name, format design, cover art, and platform setup' },
      { week: 2, title: 'Production Setup', description: 'Episode structure, recording guidelines, intro/outro production, and first episode recording' },
      { week: 3, title: 'Edit & Distribute', description: 'Audio editing, show notes writing, distribution setup, and launch preparation' },
      { week: 4, title: 'Launch & Optimize', description: 'Official launch, social promotion, performance tracking, and next episode planning' }
    ],
    testimonial: {
      quote: 'We went from zero to published in one sprint. The production quality made us sound like we had been doing this for years.',
      name: 'Podcast Client',
      title: 'Business Owner',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Podcast production is sprint-based. Launch setup is typically 20-40h; ongoing episode production is scoped per episode or per month.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Concept development, branding, recording, editing, show notes, distribution setup, social clips, and reporting.' },
      { id: 'faq-3', question: 'Do I need my own recording equipment?', answer: 'A quality USB microphone ($50-$150) and a quiet space are sufficient. We can also recommend setups or use our studio for local clients.' },
      { id: 'faq-4', question: 'How long does each episode take to produce?', answer: 'Typically 4-8h per episode including editing, show notes, and social clips. We scope ongoing production monthly.' },
      { id: 'faq-5', question: 'Which platforms will my podcast be on?', answer: 'We distribute to Spotify, Apple Podcasts, Google Podcasts, Amazon Music, and all major platforms via RSS.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Podcast hosting platform (~$15-$20/month), recording software if needed, and any guest booking tools.' },
      { id: 'faq-7', question: 'Can you help me grow my audience?', answer: 'Yes. We create social clips and audiograms for each episode and can add paid promotion as a module.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['content', 'social-media', 'branding-design']
  },

  // ── Strategy & Management ────────────────────────────────────────────────
  {
    id: 'market-research',
    slug: 'market-research',
    name: 'Deep/Wide Market Research',
    category: 'Strategy & Management',
    categoryId: 'strategy-management',
    icon: 'MagnifyingGlassCircleIcon',
    outcomeHeadline: 'Make decisions backed by real market intelligence, not assumptions',
    subhead: 'Comprehensive market research — deep dives into specific segments or wide scans across an entire industry — delivered as actionable intelligence.',
    typicalHours: '20-40h',
    bestFor: [
      'Companies entering a new market or launching a new product',
      'Businesses validating pricing, positioning, or demand',
      'Founders needing investor-grade market analysis',
      'Teams making major strategic decisions'
    ],
    notIdealFor: [
      'Businesses needing a quick Google search (this is deep research)',
      'Companies with no intention of acting on the findings'
    ],
    prerequisites: [
      'Clear research question or decision to be informed',
      'Industry or market to be researched',
      'Timeline and intended use of findings'
    ],
    whatYouGet: [
      'Structured research report with executive summary',
      'Competitive landscape and market sizing',
      'Actionable recommendations based on findings'
    ],
    deliverables: [
      'Research brief and methodology',
      'Market sizing and TAM/SAM/SOM analysis',
      'Competitive landscape mapping',
      'Customer segment analysis and personas',
      'Trend analysis and market dynamics',
      'Pricing benchmarks and positioning gaps',
      'Executive summary with key findings',
      'Recommendations and strategic implications'
    ],
    modules: [
      { name: 'Primary Research', description: 'Customer interviews, surveys, or focus groups to supplement secondary research', typicalHours: '15-25h' },
      { name: 'Competitor Deep Dive', description: 'Detailed analysis of 3-5 specific competitors including positioning, pricing, and strategy', typicalHours: '10-15h' },
      { name: 'Research Presentation', description: 'Executive presentation of findings for board or investor meetings', typicalHours: '5-8h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Scoping & Secondary Research', description: 'Research brief, methodology, market sizing, and initial data gathering for candidate sites' },
      { week: 2, title: 'Competitive & Customer Analysis', description: 'Competitor mapping, customer segment analysis, and trend research' },
      { week: 3, title: 'Synthesis & Insights', description: 'Data synthesis, pattern identification, and insight development' },
      { week: 4, title: 'Report & Recommendations', description: 'Report writing, executive summary, and strategic recommendations delivery' }
    ],
    testimonial: {
      quote: 'The research they delivered was more thorough than anything our internal team had produced. It directly shaped our go-to-market strategy.',
      name: 'Market Research Client',
      title: 'VP of Strategy',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Market research is scoped as a project. Typical range: 20-40h depending on depth, breadth, and number of markets.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Research, data gathering, analysis, synthesis, report writing, and presentation preparation.' },
      { id: 'faq-3', question: 'What is the difference between deep and wide research?', answer: 'Deep research focuses intensely on one specific segment, competitor, or question. Wide research scans an entire industry for trends, gaps, and opportunities. We scope based on your decision.' },
      { id: 'faq-4', question: 'Do you conduct primary research (interviews, surveys)?', answer: 'Yes, as a module add-on. Primary research adds significant depth but also time and cost. We recommend it for high-stakes decisions.' },
      { id: 'faq-5', question: 'What format is the deliverable?', answer: 'A structured written report with an executive summary, plus supporting data. We can also deliver as a slide deck for presentations.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Premium research databases (Statista, IBISWorld) if needed for the analysis. We include these in the project scope estimate.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with strategy, product, and marketing teams and can present findings directly to stakeholders.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['strategy', 'operational-strategy-consultation', 'pricing-analysis']
  },
  {
    id: 'influencer-sourcing',
    slug: 'influencer-sourcing',
    name: 'Influencer Sourcing',
    category: 'Strategy & Management',
    categoryId: 'strategy-management',
    icon: 'UserGroupIcon',
    outcomeHeadline: 'Find the right influencers who actually move the needle for your brand',
    subhead: 'Research-driven influencer identification, vetting, and outreach across YouTube, Instagram, TikTok, and beyond.',
    typicalHours: '15-25h',
    bestFor: [
      'Brands launching influencer marketing for the first time',
      'Companies needing vetted influencer lists for specific niches',
      'Businesses wanting to scale influencer partnerships without the guesswork',
      'E-commerce brands targeting YouTube or TikTok audiences'
    ],
    notIdealFor: [
      'Brands without a clear product or offer to promote',
      'Companies expecting influencer results without a budget for partnerships'
    ],
    prerequisites: [
      'Clear target audience and campaign objective',
      'Budget range for influencer partnerships',
      'Product or service ready to promote'
    ],
    whatYouGet: [
      'A vetted list of influencers matched to your brand and audience',
      'Engagement rate analysis and audience quality scores',
      'Outreach templates and initial contact initiated'
    ],
    deliverables: [
      'Influencer criteria definition and brief',
      'Platform-specific research (YouTube, Instagram, TikTok, etc.)',
      'Influencer list with profiles, metrics, and contact info',
      'Audience quality and engagement rate analysis',
      'Niche alignment and brand safety vetting',
      'Outreach templates and initial contact',
      'Partnership rate benchmarks',
      'Shortlist with recommendations'
    ],
    modules: [
      { name: 'Outreach Campaign', description: 'Full outreach campaign management — pitching, negotiating, and securing partnerships', typicalHours: '15-20h' },
      { name: 'Campaign Brief Creation', description: 'Detailed creative brief for influencers including talking points, dos/don\'ts, and deliverables', typicalHours: '5-8h' },
      { name: 'Performance Tracking', description: 'Post-campaign tracking of reach, engagement, and conversion from influencer content', typicalHours: '8-12h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Criteria & Research', description: 'Influencer criteria definition, platform research, and initial list building' },
      { week: 2, title: 'Vetting & Analysis', description: 'Audience quality analysis, engagement rate review, brand safety vetting, and shortlisting' },
      { week: 3, title: 'Outreach Prep', description: 'Outreach templates, rate benchmarking, and initial contact with top candidates' },
      { week: 4, title: 'Delivery & Handoff', description: 'Final vetted list, recommendations, and handoff for partnership negotiations' }
    ],
    testimonial: {
      quote: 'They found us 12 YouTube influencers in our exact niche within two weeks. Three of them became long-term partners.',
      name: 'Influencer Sourcing Client',
      title: 'Marketing Director',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Influencer sourcing is scoped as a project. Typical range: 15-25h depending on number of platforms, niche complexity, and list size.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Research, vetting, audience analysis, outreach template creation, and list compilation.' },
      { id: 'faq-3', question: 'What platforms do you research?', answer: 'YouTube, Instagram, TikTok, Twitter/X, LinkedIn, and podcasts. We focus on the platforms most relevant to your audience.' },
      { id: 'faq-4', question: 'How many influencers will I get?', answer: 'Typically 20-50 researched, narrowed to a shortlist of 10-15 vetted recommendations. We scope based on your needs.' },
      { id: 'faq-5', question: 'Do you handle negotiations?', answer: 'Outreach and negotiation is a separate module. Sourcing delivers the vetted list; partnership management is an add-on.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Influencer research tools (e.g., Modash, Upfluence) if needed, and influencer partnership fees (paid directly to influencers).' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We deliver the vetted list and outreach templates; your team or ours can manage the partnership from there.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['affiliate-marketing', 'social-media', 'paid-social']
  },

  // ── Specialized Services ─────────────────────────────────────────────────
  {
    id: 'pdf-translation',
    slug: 'pdf-translation',
    name: 'PDF Translation',
    category: 'Specialized Services',
    categoryId: 'specialized-services',
    icon: 'DocumentDuplicateIcon',
    outcomeHeadline: 'Reach new markets with documents that look identical — just in a different language',
    subhead: 'Professional PDF translation that preserves your original layout, design, and formatting exactly while converting content to the target language.',
    typicalHours: '5-15h',
    bestFor: [
      'Businesses expanding into Spanish, French, Portuguese, or other language markets',
      'Companies with branded PDFs, brochures, or catalogs needing translation',
      'Legal, medical, or technical documents requiring accurate translation',
      'Brands wanting multilingual marketing materials without redesigning from scratch'
    ],
    notIdealFor: [
      'Real-time or live document translation needs',
      'Extremely high-volume translation projects (100+ pages per sprint)'
    ],
    prerequisites: [
      'Original PDF document in editable or high-quality format',
      'Target language(s) specified',
      'Any brand terminology or glossary to maintain consistency'
    ],
    whatYouGet: [
      'Translated PDF that matches the original layout pixel-for-pixel',
      'Accurate translation by native or professional translators',
      'Consistent brand terminology throughout'
    ],
    deliverables: [
      'Translated PDF in target language(s)',
      'Layout and design preservation',
      'Brand terminology consistency',
      'Proofreading and quality check',
      'Editable source file (if applicable)',
      'Multiple language versions (if requested)',
      'Glossary of translated terms',
      'Delivery in original PDF format'
    ],
    modules: [
      { name: 'Multi-Language Pack', description: 'Translation into 3+ languages with consistent terminology across all versions', typicalHours: '12-20h' },
      { name: 'Legal/Technical Review', description: 'Additional review by subject matter expert for legal, medical, or technical documents', typicalHours: '4-8h' },
      { name: 'Document Redesign', description: 'Full redesign of the PDF if the original layout needs updating alongside translation', typicalHours: '10-20h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Document Review & Scoping', description: 'Document analysis, word count, complexity assessment, and translator assignment' },
      { week: 2, title: 'Translation', description: 'Professional translation, terminology consistency, and initial draft completion' },
      { week: 3, title: 'Layout & QC', description: 'Layout preservation, formatting adjustments, and quality control review' },
      { week: 4, title: 'Final Delivery', description: 'Final proofread, client review, revisions, and delivery in all requested formats' }
    ],
    testimonial: {
      quote: 'Our entire product catalog was translated into Spanish and it looked identical to the original. Our Latin American partners were impressed.',
      name: 'PDF Translation Client',
      title: 'Operations Manager',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. PDF translation is scoped by document length and complexity. Typical range: 5-15h per document depending on page count and technical content.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Translation, layout preservation, formatting, proofreading, quality control, and client revisions.' },
      { id: 'faq-3', question: 'What languages do you translate to?', answer: 'Spanish, French, Portuguese, German, Italian, and other major languages. Contact us for less common languages — we source qualified translators.' },
      { id: 'faq-4', question: 'Will the layout look exactly the same?', answer: 'Yes. We preserve the original design, fonts, images, and layout. Some minor adjustments may be needed for text expansion in certain languages.' },
      { id: 'faq-5', question: 'Do you use machine translation?', answer: 'No. All translations are done by professional human translators. We may use AI-assisted tools for efficiency but all output is reviewed and edited by a human.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'None. Translation and layout work are included in the project scope. Certified translation for legal documents may require additional fees.' },
      { id: 'faq-7', question: 'Can you translate multiple documents at once?', answer: 'Yes. We can batch multiple documents in a single sprint. We will scope based on total word count and complexity.' }
    ],
    capacityFit: 'light',
    relatedServices: ['content', 'branding-design', 'premium-print-cards']
  },

  // ── Business Operations ──────────────────────────────────────────────────
  {
    id: 'internal-operational-structure',
    slug: 'internal-operational-structure',
    name: 'Internal Operational Structure',
    category: 'Business Operations',
    categoryId: 'business-operations',
    icon: 'BuildingOfficeIcon',
    outcomeHeadline: 'Build a business that runs without you in every decision',
    subhead: 'Organizational design and operational structure consulting that defines roles, reporting lines, and workflows so your team executes with clarity.',
    typicalHours: '20-40h',
    bestFor: [
      'Founders scaling from 1-5 to 10+ employees',
      'Businesses experiencing growing pains and unclear accountability',
      'Companies preparing for investment or acquisition',
      'Teams where everything still runs through the founder'
    ],
    notIdealFor: [
      'Solo operators with no plans to hire',
      'Companies with a fully functioning org structure already in place'
    ],
    prerequisites: [
      'Current team roster and roles (even informal ones)',
      'Business goals and growth targets for the next 12 months',
      'Founder availability for 2-3 working sessions'
    ],
    whatYouGet: [
      'A clear org chart with defined roles and reporting lines',
      'Role clarity documents so everyone knows what they own',
      'An operational structure that scales with your growth'
    ],
    deliverables: [
      'Current state assessment and gap analysis',
      'Organizational chart design',
      'Role definition documents (responsibilities, KPIs, reporting)',
      'Reporting structure and communication cadence',
      'Decision-making framework (who decides what)',
      'Hiring roadmap for next 6-12 months',
      'Onboarding structure recommendations',
      'Implementation guide and rollout plan'
    ],
    modules: [
      { name: 'Team Workshop', description: 'Facilitated working session with leadership team to align on structure and roles', typicalHours: '6-10h' },
      { name: 'Job Description Writing', description: 'Professional job descriptions for all defined roles', typicalHours: '8-12h' },
      { name: 'Hiring Process Design', description: 'Interview process, scoring rubrics, and candidate evaluation framework', typicalHours: '8-12h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Assessment & Discovery', description: 'Current state review, team interviews, gap analysis, and structural goals alignment' },
      { week: 2, title: 'Structure Design', description: 'Org chart development, role definition, reporting lines, and decision framework' },
      { week: 3, title: 'Documentation', description: 'Role documents, communication cadence, hiring roadmap, and onboarding structure' },
      { week: 4, title: 'Presentation & Rollout', description: 'Leadership presentation, team rollout plan, and implementation guide delivery' }
    ],
    testimonial: {
      quote: 'We went from a chaotic 8-person team where everything ran through me to a structured organization where people actually knew what they owned. Game changer.',
      name: 'Operations Client',
      title: 'Founder & CEO',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Operational structure projects are scoped upfront. Typical range: 20-40h depending on team size and complexity.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Discovery sessions, research, org chart design, documentation, presentation, and implementation planning.' },
      { id: 'faq-3', question: 'Do you help with hiring?', answer: 'We design the structure and hiring roadmap. Actual recruiting is a separate service. We can refer you to recruiting partners.' },
      { id: 'faq-4', question: 'How involved does the founder need to be?', answer: 'We need 2-3 working sessions (2-3h each) to understand the business and validate the structure. The rest we handle independently.' },
      { id: 'faq-5', question: 'What if my team resists the new structure?', answer: 'We design change management into the rollout plan. Clear communication and role clarity typically reduce resistance significantly.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Org chart software (Lucidchart, Miro) if not already in your stack — most have free tiers.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We interview key team members as part of discovery and involve leadership in the design process.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['internal-sop-assistance', 'operational-strategy-consultation', 'strategy']
  },
  {
    id: 'internal-sop-assistance',
    slug: 'internal-sop-assistance',
    name: 'Internal SOP Assistance',
    category: 'Business Operations',
    categoryId: 'business-operations',
    icon: 'ClipboardDocumentListIcon',
    outcomeHeadline: 'Document how your business works so it can run without you explaining it every time',
    subhead: 'Standard Operating Procedure development that captures your processes, reduces errors, and makes training new hires 10x faster.',
    typicalHours: '15-30h',
    bestFor: [
      'Businesses where key processes live only in the founder\'s head',
      'Companies experiencing inconsistent output or quality issues',
      'Teams onboarding new hires frequently',
      'Businesses preparing for scale, franchise, or acquisition'
    ],
    notIdealFor: [
      'Solo operators with no team or plans to hire',
      'Businesses with fully documented processes already'
    ],
    prerequisites: [
      'Identified processes or departments to document',
      'Access to team members who perform the processes',
      'Willingness to review and approve SOPs before finalization'
    ],
    whatYouGet: [
      'Clear, step-by-step SOPs your team can actually follow',
      'Consistent process execution across your organization',
      'A documentation library that makes training fast and reliable'
    ],
    deliverables: [
      'Process discovery and mapping sessions',
      'SOP writing for identified processes',
      'Step-by-step instructions with decision trees',
      'Visual process flowcharts (where applicable)',
      'Roles and responsibilities per process',
      'SOP template library for future use',
      'Version control and update protocol',
      'Implementation and training guide'
    ],
    modules: [
      { name: 'Process Audit', description: 'Full audit of existing processes to identify gaps, redundancies, and documentation priorities', typicalHours: '8-12h' },
      { name: 'Training Materials', description: 'Convert SOPs into training videos, checklists, or onboarding guides', typicalHours: '10-15h' },
      { name: 'SOP Software Setup', description: 'Setup of Notion, Trainual, or equivalent platform for SOP storage and access', typicalHours: '5-8h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Prioritization', description: 'Process inventory, stakeholder interviews, priority ranking, and SOP scope definition' },
      { week: 2, title: 'Process Mapping', description: 'Detailed process mapping, flowchart creation, and draft SOP writing begins' },
      { week: 3, title: 'SOP Writing & Review', description: 'Full SOP drafts, client review, revisions, and approval cycle' },
      { week: 4, title: 'Library & Handoff', description: 'SOP library organization, template creation, and implementation guide delivery' }
    ],
    testimonial: {
      quote: 'We cut our onboarding time in half. New hires now have a clear playbook from day one instead of shadowing someone for two weeks.',
      name: 'SOP Client',
      title: 'Operations Director',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. SOP projects are scoped by number of processes and complexity. Typical range: 15-30h for 5-10 core processes.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Discovery sessions, process mapping, SOP writing, flowchart creation, review cycles, and library setup.' },
      { id: 'faq-3', question: 'How many SOPs can you write in one sprint?', answer: 'Typically 5-10 core processes per sprint depending on complexity. We prioritize the highest-impact processes first.' },
      { id: 'faq-4', question: 'What format are the SOPs delivered in?', answer: 'Written documents with step-by-step instructions, decision trees, and flowcharts. We can deliver in Google Docs, Notion, Trainual, or your preferred platform.' },
      { id: 'faq-5', question: 'Do you need to observe our processes?', answer: 'We conduct process mapping sessions with the people who perform the work. Observation or screen recordings are helpful but not always required.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'SOP software if you want a dedicated platform (Trainual ~$49/month, Notion free tier available). We can work in any format.' },
      { id: 'faq-7', question: 'Can you update existing SOPs?', answer: 'Yes. We can audit, update, and improve existing documentation. We will assess what you have and recommend the most efficient approach.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['internal-operational-structure', 'operational-strategy-consultation', 'strategy']
  },
  {
    id: 'operational-strategy-consultation',
    slug: 'operational-strategy-consultation',
    name: 'Operational Strategy Consultation',
    category: 'Business Operations',
    categoryId: 'business-operations',
    icon: 'ChartPieIcon',
    outcomeHeadline: 'Get an outside perspective on how to run your business more profitably',
    subhead: 'Strategic operational consulting that identifies inefficiencies, unlocks capacity, and builds a roadmap for sustainable growth.',
    typicalHours: '15-30h',
    bestFor: [
      'Founders feeling stuck or overwhelmed by operations',
      'Businesses with revenue growth but shrinking margins',
      'Companies preparing for a new phase of growth',
      'Teams that are busy but not productive'
    ],
    notIdealFor: [
      'Businesses looking for marketing strategy (see Strategy service)',
      'Companies wanting execution support without strategic input'
    ],
    prerequisites: [
      'Basic financial data (revenue, costs, margins)',
      'Overview of current team and operations',
      'Founder or leadership availability for consultation sessions'
    ],
    whatYouGet: [
      'An honest assessment of your operational strengths and gaps',
      'A prioritized roadmap of operational improvements',
      'Clarity on where to focus for maximum impact'
    ],
    deliverables: [
      'Operational assessment and diagnostic',
      'Efficiency gap analysis',
      'Process bottleneck identification',
      'Resource allocation review',
      'Operational roadmap (90-day priorities)',
      'Cost reduction and margin improvement recommendations',
      'Technology and tooling recommendations',
      'Executive summary and action plan'
    ],
    modules: [
      { name: 'Financial Operations Review', description: 'Deep dive into cost structure, margins, and financial operational efficiency', typicalHours: '8-12h' },
      { name: 'Technology Stack Audit', description: 'Review of current tools and systems with recommendations for consolidation or upgrades', typicalHours: '6-10h' },
      { name: 'Quarterly Check-In', description: 'Follow-up consultation session to review progress and adjust the roadmap', typicalHours: '3-5h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Discovery & Assessment', description: 'Business overview, operational data review, team interviews, and diagnostic framework' },
      { week: 2, title: 'Analysis & Diagnosis', description: 'Gap analysis, bottleneck identification, resource review, and opportunity mapping' },
      { week: 3, title: 'Strategy Development', description: 'Roadmap creation, recommendation development, and prioritization framework' },
      { week: 4, title: 'Presentation & Action Plan', description: 'Executive presentation, action plan delivery, and implementation guidance' }
    ],
    testimonial: {
      quote: 'In one sprint they identified three operational bottlenecks that were costing us 20% of our capacity. The ROI on this engagement was immediate.',
      name: 'Operations Consultation Client',
      title: 'CEO',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Operational strategy consultations are scoped as projects. Typical range: 15-30h depending on business complexity and scope.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Discovery sessions, data analysis, research, roadmap development, documentation, and presentation.' },
      { id: 'faq-3', question: 'How is this different from the Strategy service?', answer: 'The Strategy service focuses on marketing strategy. This service focuses on internal business operations — how you run the company, not how you market it.' },
      { id: 'faq-4', question: 'Do you implement the recommendations?', answer: 'Implementation is separate. This service delivers the strategy and roadmap. Execution can be scoped as a follow-on engagement.' },
      { id: 'faq-5', question: 'How much access do you need to our business?', answer: 'We need financial data, operational overview, and 2-3 sessions with leadership. We keep all information strictly confidential.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'None. This is a consulting engagement. Any tool recommendations are optional and at your discretion.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We interview key team members as part of discovery and present findings to leadership for alignment.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['internal-operational-structure', 'internal-sop-assistance', 'pricing-analysis']
  },
  {
    id: 'pricing-analysis',
    slug: 'pricing-analysis',
    name: 'Pricing Analysis and Suggestion',
    category: 'Business Operations',
    categoryId: 'business-operations',
    icon: 'CurrencyDollarIcon',
    outcomeHeadline: 'Stop leaving money on the table with pricing that reflects your true value',
    subhead: 'Data-driven pricing analysis that benchmarks your rates against the market and recommends a pricing strategy built to maximize revenue and retention.',
    typicalHours: '10-20h',
    bestFor: [
      'Businesses unsure if they are priced too low or too high',
      'Companies launching new products or services',
      'Brands experiencing high churn or price objections',
      'Businesses preparing to raise prices without losing customers'
    ],
    notIdealFor: [
      'Businesses with commodity products where pricing is purely market-driven',
      'Companies unwilling to consider pricing changes'
    ],
    prerequisites: [
      'Current pricing structure and product/service catalog',
      'Basic cost data (COGS, overhead)',
      'Target customer profile and competitive context'
    ],
    whatYouGet: [
      'A clear picture of where your pricing stands vs. the market',
      'Specific pricing recommendations with rationale',
      'A strategy for implementing changes without disrupting existing customers'
    ],
    deliverables: [
      'Current pricing audit and analysis',
      'Competitive pricing benchmarks',
      'Value-based pricing assessment',
      'Price elasticity analysis',
      'Pricing model recommendations (tiered, bundled, usage-based, etc.)',
      'Revenue impact projections',
      'Price increase communication strategy',
      'Implementation roadmap'
    ],
    modules: [
      { name: 'Customer Willingness-to-Pay Research', description: 'Surveys or interviews to gauge customer price sensitivity and perceived value', typicalHours: '8-12h' },
      { name: 'Pricing Page Redesign', description: 'Redesign of pricing page to better communicate value and reduce friction', typicalHours: '10-15h' },
      { name: 'A/B Price Testing Setup', description: 'Setup of price testing framework to validate recommendations with real data', typicalHours: '6-10h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Audit & Benchmarking', description: 'Current pricing review, competitive benchmarking, and market rate analysis' },
      { week: 2, title: 'Value Analysis', description: 'Value-based pricing assessment, customer segment analysis, and elasticity modeling' },
      { week: 3, title: 'Recommendations', description: 'Pricing model development, revenue projections, and strategy formulation' },
      { week: 4, title: 'Delivery & Roadmap', description: 'Recommendations presentation, implementation roadmap, and communication strategy' }
    ],
    testimonial: {
      quote: 'We raised prices by 30% using their recommendations and actually saw churn go down. The analysis gave us the confidence to charge what we were worth.',
      name: 'Pricing Analysis Client',
      title: 'Founder',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Pricing analysis is scoped as a project. Typical range: 10-20h depending on number of products, market complexity, and depth of analysis.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Pricing audit, competitive research, analysis, modeling, recommendations, and presentation.' },
      { id: 'faq-3', question: 'Will you tell me to raise my prices?', answer: 'We tell you what the data suggests. Sometimes that means raising prices, sometimes restructuring, sometimes adding tiers. We follow the analysis.' },
      { id: 'faq-4', question: 'How do you handle price increases with existing customers?', answer: 'We include a communication strategy in the deliverables — how to announce changes, grandfather existing clients, and minimize churn.' },
      { id: 'faq-5', question: 'Do you test pricing?', answer: 'A/B price testing is a separate module. The core service delivers recommendations; testing validates them with real data.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Survey tools if primary research is included, and any A/B testing platform fees if that module is added.' },
      { id: 'faq-7', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with finance, sales, and product teams to ensure recommendations are grounded in operational reality.' }
    ],
    capacityFit: 'light',
    relatedServices: ['operational-strategy-consultation', 'market-research', 'strategy']
  },
  {
    id: 'location-analysis',
    slug: 'location-analysis',
    name: 'Location Analysis for Brick & Mortar',
    category: 'Business Operations',
    categoryId: 'business-operations',
    icon: 'MapPinIcon',
    outcomeHeadline: 'Open in the right location the first time — backed by data, not gut feel',
    subhead: 'Comprehensive location intelligence for retail, restaurant, and brick-and-mortar businesses — foot traffic, demographics, competition, and site scoring.',
    typicalHours: '15-30h',
    bestFor: [
      'Retail and restaurant businesses scouting new locations',
      'Franchises evaluating market expansion',
      'Businesses considering relocation',
      'Investors evaluating brick-and-mortar opportunities'
    ],
    notIdealFor: [
      'Online-only businesses with no physical presence',
      'Businesses with a location already locked in'
    ],
    prerequisites: [
      'Target city, region, or list of candidate locations',
      'Business type and customer profile',
      'Budget range and size requirements for the space'
    ],
    whatYouGet: [
      'A scored comparison of candidate locations with clear recommendation',
      'Foot traffic, demographic, and competitive data for each site',
      'A decision framework you can use for future expansion'
    ],
    deliverables: [
      'Location criteria definition and scoring framework',
      'Foot traffic and pedestrian data analysis',
      'Demographic and psychographic profile of each area',
      'Competitive density mapping',
      'Lease rate benchmarks and cost analysis',
      'Accessibility and visibility assessment',
      'Site scoring and ranked recommendations',
      'Executive summary with final recommendation'
    ],
    modules: [
      { name: 'On-Site Visit Report', description: 'In-person or virtual site visit with detailed observations and photos', typicalHours: '6-10h' },
      { name: 'Multi-Market Comparison', description: 'Location analysis across 2+ cities or markets for expansion planning', typicalHours: '15-25h' },
      { name: 'Lease Negotiation Brief', description: 'Market rate analysis and negotiation talking points for lease discussions', typicalHours: '5-8h' }
    ],
    exampleSprint: [
      { week: 1, title: 'Criteria & Research Setup', description: 'Location criteria definition, scoring framework, and initial data gathering for candidate sites' },
      { week: 2, title: 'Data Analysis', description: 'Foot traffic analysis, demographic profiling, competitive mapping, and lease benchmarking' },
      { week: 3, title: 'Scoring & Comparison', description: 'Site scoring, comparative analysis, and recommendation development' },
      { week: 4, title: 'Report & Recommendation', description: 'Executive report delivery, ranked recommendations, and decision framework handoff' }
    ],
    testimonial: {
      quote: 'They analyzed 6 potential locations and gave us a clear winner with the data to back it up. We opened there and hit our first-month revenue target.',
      name: 'Location Analysis Client',
      title: 'Restaurant Owner',
      company: 'Best Odds Corp'
    },
    faq: [
      { id: 'faq-1', question: 'Do you bill hourly?', answer: 'No. Location analysis is scoped as a project. Typical range: 15-30h depending on number of locations and depth of analysis.' },
      { id: 'faq-2', question: 'What counts as hours?', answer: 'Criteria development, data research, foot traffic analysis, demographic profiling, competitive mapping, scoring, and report writing.' },
      { id: 'faq-3', question: 'How many locations can you analyze?', answer: 'Typically 3-6 candidate locations per sprint. More locations can be scoped across multiple sprints.' },
      { id: 'faq-4', question: 'Do you visit the locations in person?', answer: 'On-site visits are a separate module. The core analysis uses data sources, satellite imagery, and street-level tools. In-person visits add significant depth.' },
      { id: 'faq-5', question: 'What data sources do you use?', answer: 'Foot traffic data (Placer.ai, SafeGraph), demographic data (Census, ESRI), competitive data, and lease rate databases.' },
      { id: 'faq-6', question: 'What third-party costs should I expect?', answer: 'Location data platform fees (Placer.ai, ESRI) if needed for the analysis. We include these in the project scope estimate.' },
      { id: 'faq-7', question: 'What third-party costs should I expect?', answer: 'Survey tools if primary research is included, and any A/B testing platform fees if that module is added.' },
      { id: 'faq-8', question: 'Can you work with my existing team?', answer: 'Yes. We collaborate with finance, sales, and product teams to ensure recommendations are grounded in operational reality.' }
    ],
    capacityFit: 'standard',
    relatedServices: ['operational-strategy-consultation', 'market-research', 'out-of-home-marketing']
  }
];

servicesData.push(...newServices);

export function getServiceBySlug(slug: string): ServiceData | undefined {
  return servicesData.find(service => service.slug === slug);
}

export function getServicesByCategory(categoryId: string): ServiceData[] {
  return servicesData.filter(service => service.categoryId === categoryId);
}

export function getRelatedServices(serviceSlug: string): ServiceData[] {
  const service = getServiceBySlug(serviceSlug);
  if (!service) return [];
  
  return service.relatedServices
    .map(slug => getServiceBySlug(slug))
    .filter((s): s is ServiceData => s !== undefined)
    .slice(0, 3);
}